import { SqlExercise } from '../../types/sql';

export const sqlExercises: SqlExercise[] = [
  // NIVEL PRE-BÁSICO
  {
    id: 1,
    level: 'NIVEL PRE-BÁSICO',
    title: 'Ejercicio 1: Listar Todos los Vendedores',
    description: 'Mostrar todos los vendedores con su información básica, ordenados alfabéticamente por apellido y nombre.',
    expectedColumns: ['id_vendedor', 'nombre', 'apellido', 'departamento', 'salario_base'],
    solution: `SELECT 
    id_vendedor,
    nombre,
    apellido,
    departamento,
    salario_base
FROM vendedores
ORDER BY apellido, nombre;`,
    difficulty: 'pre-basic',
    why: {
      title: '¿Por qué usar SELECT aquí?',
      content: `**SELECT** es el comando fundamental de SQL para consultar datos. Es la herramienta que te permite "preguntarle" a la base de datos qué información necesitas.

En este ejercicio, usamos **SELECT** porque queremos **obtener información** de la tabla de vendedores, no modificarla ni eliminarla. SELECT es la forma de leer datos de una base de datos.

### ¿Cuándo usar SELECT?
- Cuando necesitas **ver** información almacenada
- Cuando quieres **filtrar** datos según criterios
- Cuando necesitas **calcular** valores a partir de los datos
- Cuando quieres **combinar** información de múltiples tablas

### En el mundo real:
Cada vez que abres una aplicación y ves una lista (productos en un e-commerce, empleados en un sistema de RRHH, transacciones en un banco), detrás hay un **SELECT** ejecutándose para traerte esa información.`,
      keyPoints: [
        'SELECT es solo lectura: no modifica datos, solo los consulta',
        'Es el comando más usado en SQL (80% de las queries son SELECT)',
        'Permite especificar exactamente qué columnas necesitas',
        'Puedes combinar SELECT con WHERE, ORDER BY, GROUP BY, etc.'
      ],
      realWorldExample: 'Cuando un gerente necesita ver la lista completa de vendedores para una reunión, ejecuta un SELECT similar a este ejercicio.'
    },
    theory: `# SELECT: El Comando Fundamental de SQL

## ¿Qué es SELECT?

**SELECT** es el comando más importante de SQL. Te permite consultar y recuperar datos de una o más tablas en una base de datos.

### Sintaxis Básica:
\`\`\`sql
SELECT columnas
FROM tabla
WHERE condiciones
ORDER BY columnas;
\`\`\`

## Componentes del SELECT

### 1. SELECT (qué columnas)
Especifica qué columnas quieres ver en el resultado:
- Puedes seleccionar columnas específicas: \`SELECT nombre, apellido\`
- O todas las columnas: \`SELECT *\`

### 2. FROM (de qué tabla)
Indica de qué tabla(s) provienen los datos:
\`\`\`sql
FROM vendedores
\`\`\`

### 3. ORDER BY (ordenamiento)
Ordena los resultados según una o más columnas:
- Ascendente (por defecto): \`ORDER BY apellido ASC\`
- Descendente: \`ORDER BY salario DESC\`
- Múltiples columnas: \`ORDER BY apellido, nombre\`

## Ejemplo Completo

\`\`\`sql
SELECT 
    id_vendedor,
    nombre,
    apellido,
    departamento,
    salario_base
FROM vendedores
ORDER BY apellido, nombre;
\`\`\`

Este query:
1. Selecciona 5 columnas específicas
2. De la tabla \`vendedores\`
3. Ordena primero por apellido, luego por nombre (ambos ascendentes)`,
    tips: [
      'Siempre especifica las columnas que necesitas en lugar de usar SELECT *',
      'ORDER BY puede ordenar por múltiples columnas separadas por comas',
      'El orden importa: primero ordena por la primera columna, luego por la segunda si hay empates'
    ],
    advice: [
      'Practica escribiendo SELECT con diferentes combinaciones de columnas',
      'Experimenta con ORDER BY usando diferentes columnas y direcciones (ASC/DESC)',
      'Usa nombres descriptivos para las columnas en tus resultados'
    ]
  },
  {
    id: 2,
    level: 'NIVEL PRE-BÁSICO',
    title: 'Ejercicio 2: Vendedores de un Departamento Específico',
    description: 'Filtrar y mostrar únicamente los vendedores del departamento \'Electrónicos\', ordenados por fecha de ingreso.',
    expectedColumns: ['nombre', 'apellido', 'departamento', 'fecha_ingreso'],
    solution: `SELECT 
    nombre,
    apellido,
    departamento,
    fecha_ingreso
FROM vendedores
WHERE departamento = 'Electrónicos'
ORDER BY fecha_ingreso;`,
    difficulty: 'pre-basic',
    why: {
      title: '¿Por qué usar WHERE aquí?',
      content: `**WHERE** es la cláusula que te permite **filtrar** datos según condiciones específicas. Sin WHERE, obtendrías TODOS los registros de la tabla, lo cual puede ser ineficiente y poco útil.

En este ejercicio, usamos **WHERE** porque solo necesitamos los vendedores de un departamento específico ('Electrónicos'), no todos los vendedores de la empresa.

### ¿Cuándo usar WHERE?
- Cuando necesitas **filtrar** registros según un criterio específico
- Cuando quieres trabajar con un **subconjunto** de datos en lugar de toda la tabla
- Cuando necesitas encontrar registros que cumplan **condiciones** específicas
- Cuando quieres **optimizar** tus consultas trabajando solo con datos relevantes

### En el mundo real:
Cuando un gerente de Recursos Humanos necesita ver solo los empleados de un departamento específico para una evaluación, usa WHERE para filtrar. Sin WHERE, vería todos los empleados de la empresa, lo cual sería ineficiente.`,
      keyPoints: [
        'WHERE filtra ANTES de ordenar o agrupar los datos',
        'Puedes usar múltiples condiciones con AND, OR, NOT',
        'WHERE es más eficiente que filtrar en la aplicación después de obtener todos los datos',
        'Las condiciones en WHERE pueden usar operadores: =, <>, >, <, >=, <=, LIKE, IN, BETWEEN'
      ],
      realWorldExample: 'Cuando un director de ventas necesita ver solo los vendedores de "Electrónicos" para una reunión de equipo, ejecuta un SELECT con WHERE departamento = \'Electrónicos\'.'
    },
    theory: `# WHERE: Filtrando Datos con Condiciones

## ¿Qué es WHERE?

**WHERE** es una cláusula de SQL que te permite filtrar filas según condiciones específicas. Solo las filas que cumplen la condición serán incluidas en el resultado.

### Sintaxis Básica:
\`\`\`sql
SELECT columnas
FROM tabla
WHERE condicion
ORDER BY columnas;
\`\`\`

## Operadores de Comparación

### Igualdad (=)
Compara si dos valores son iguales:
\`\`\`sql
WHERE departamento = 'Electrónicos'
\`\`\`

### Otros Operadores Comunes:
- **<>** o **!=**: Diferente de
- **>**: Mayor que
- **<**: Menor que
- **>=**: Mayor o igual que
- **<=**: Menor o igual que
- **LIKE**: Coincidencia de patrón (con % y _)
- **IN**: Dentro de una lista de valores
- **BETWEEN**: Entre dos valores

## Combinando Condiciones

### AND (Y)
Ambas condiciones deben cumplirse:
\`\`\`sql
WHERE departamento = 'Electrónicos' AND salario_base > 3000
\`\`\`

### OR (O)
Al menos una condición debe cumplirse:
\`\`\`sql
WHERE departamento = 'Electrónicos' OR departamento = 'Ropa'
\`\`\`

### NOT (NO)
Niega una condición:
\`\`\`sql
WHERE NOT departamento = 'Electrónicos'
\`\`\`

## Ejemplo Completo

\`\`\`sql
SELECT 
    nombre,
    apellido,
    departamento,
    fecha_ingreso
FROM vendedores
WHERE departamento = 'Electrónicos'
ORDER BY fecha_ingreso;
\`\`\`

Este query:
1. Selecciona 4 columnas específicas
2. De la tabla \`vendedores\`
3. **Filtra** solo los registros donde departamento = 'Electrónicos'
4. Ordena los resultados por fecha_ingreso (ascendente)

## Orden de Ejecución

SQL ejecuta las cláusulas en este orden:
1. **FROM**: Identifica la tabla
2. **WHERE**: Filtra las filas (reduce el conjunto de datos)
3. **SELECT**: Selecciona las columnas
4. **ORDER BY**: Ordena los resultados

Por eso WHERE es tan importante: reduce el trabajo que SQL debe hacer antes de ordenar o procesar los datos.`,
    tips: [
      'Siempre usa WHERE cuando necesites filtrar datos, no lo hagas en tu aplicación',
      'Las cadenas de texto en WHERE deben ir entre comillas simples (\'texto\')',
      'WHERE se ejecuta ANTES de ORDER BY, lo que hace las consultas más eficientes',
      'Puedes combinar múltiples condiciones con AND/OR según necesites'
    ],
    advice: [
      'Practica filtrando por diferentes columnas y valores',
      'Experimenta con diferentes operadores de comparación',
      'Recuerda que WHERE filtra filas, no columnas (eso lo hace SELECT)',
      'Usa WHERE para trabajar solo con los datos que realmente necesitas'
    ]
  },
  {
    id: 3,
    level: 'NIVEL PRE-BÁSICO',
    title: 'Ejercicio 3: Top 5 Productos Más Caros',
    description: 'Identificar los 5 productos con el precio unitario más alto.',
    expectedColumns: ['nombre_producto', 'categoria', 'precio_unitario'],
    solution: `SELECT 
    nombre_producto,
    categoria,
    precio_unitario
FROM productos
ORDER BY precio_unitario DESC
LIMIT 5;`,
    difficulty: 'pre-basic',
    why: {
      title: '¿Por qué usar LIMIT aquí?',
      content: `**LIMIT** te permite restringir el número de filas que devuelve tu consulta. Es especialmente útil cuando solo necesitas los primeros N resultados, como los "top 5" o los "mejores 10".

En este ejercicio, usamos **LIMIT 5** porque solo necesitamos los 5 productos más caros, no todos los productos de la base de datos. Esto hace la consulta más eficiente y el resultado más manejable.

### ¿Cuándo usar LIMIT?
- Cuando necesitas solo los **primeros N** resultados
- Cuando quieres implementar **paginación** (LIMIT + OFFSET)
- Cuando necesitas los **top N** de una lista ordenada
- Cuando quieres **limitar** el tamaño del resultado para mejorar performance

### En el mundo real:
Cuando un gerente de producto necesita ver los 5 productos más caros para una reunión de pricing, usa LIMIT 5. Sin LIMIT, vería todos los productos y tendría que buscar manualmente los más caros.`,
      keyPoints: [
        'LIMIT siempre va al final de la consulta, después de ORDER BY',
        'LIMIT reduce el trabajo del servidor al procesar menos datos',
        'Puedes combinar LIMIT con OFFSET para paginación',
        'LIMIT es muy útil para "top N" queries cuando tienes muchos datos'
      ],
      realWorldExample: 'Cuando un analista necesita los 10 clientes que más compraron este mes, usa ORDER BY total DESC LIMIT 10 para obtener solo esos 10, no todos los clientes.'
    },
    theory: `# LIMIT: Restringiendo el Número de Resultados

## ¿Qué es LIMIT?

**LIMIT** es una cláusula que restringe el número de filas que devuelve una consulta. Te permite obtener solo los primeros N resultados.

### Sintaxis Básica:
\`\`\`sql
SELECT columnas
FROM tabla
ORDER BY columna DESC
LIMIT numero;
\`\`\`

## Uso Básico de LIMIT

### Top N Resultados
Obtener los primeros N registros:
\`\`\`sql
SELECT * FROM productos
ORDER BY precio_unitario DESC
LIMIT 5;
\`\`\`
Esto devuelve los 5 productos más caros.

### LIMIT con OFFSET (Paginación)
Obtener resultados desde una posición específica:
\`\`\`sql
SELECT * FROM productos
ORDER BY precio_unitario DESC
LIMIT 10 OFFSET 20;
\`\`\`
Esto devuelve 10 productos, empezando desde el registro 21 (salta los primeros 20).

### Sintaxis Alternativa
También puedes usar la sintaxis con coma:
\`\`\`sql
LIMIT 20, 10  -- OFFSET 20, LIMIT 10
\`\`\`

## Combinando LIMIT con ORDER BY

**LIMIT casi siempre se usa con ORDER BY** para obtener resultados ordenados:

\`\`\`sql
SELECT 
    nombre_producto,
    categoria,
    precio_unitario
FROM productos
ORDER BY precio_unitario DESC
LIMIT 5;
\`\`\`

Este query:
1. Selecciona las columnas especificadas
2. De la tabla \`productos\`
3. **Ordena** por precio_unitario descendente (más caros primero)
4. **Limita** el resultado a solo 5 filas

## Orden de Ejecución

SQL ejecuta las cláusulas en este orden:
1. **FROM**: Identifica la tabla
2. **WHERE**: Filtra las filas (si existe)
3. **SELECT**: Selecciona las columnas
4. **ORDER BY**: Ordena los resultados
5. **LIMIT**: Restringe el número de filas

Por eso LIMIT va al final: primero ordena todo, luego toma solo las primeras N filas.

## Casos de Uso Comunes

### Top N Queries
\`\`\`sql
-- Top 10 productos más vendidos
SELECT producto, SUM(cantidad) as total_vendido
FROM ventas
GROUP BY producto
ORDER BY total_vendido DESC
LIMIT 10;
\`\`\`

### Paginación
\`\`\`sql
-- Página 1 (primeros 20)
SELECT * FROM productos LIMIT 20 OFFSET 0;

-- Página 2 (siguientes 20)
SELECT * FROM productos LIMIT 20 OFFSET 20;

-- Página 3 (siguientes 20)
SELECT * FROM productos LIMIT 20 OFFSET 40;
\`\`\`

## Ventajas de LIMIT

- **Performance**: Procesa menos datos
- **Eficiencia**: Reduce transferencia de datos
- **UX**: Resultados más manejables para el usuario
- **Costo**: Menor uso de recursos del servidor`,
    tips: [
      'LIMIT siempre va al final de la consulta, después de ORDER BY',
      'Usa LIMIT con ORDER BY para obtener "top N" resultados',
      'LIMIT sin ORDER BY puede dar resultados inconsistentes',
      'Para paginación, usa LIMIT + OFFSET o la sintaxis LIMIT offset, count'
    ],
    advice: [
      'Siempre usa ORDER BY antes de LIMIT para resultados predecibles',
      'LIMIT es muy útil para dashboards y reportes que muestran "top N"',
      'Considera usar LIMIT incluso cuando no es estrictamente necesario para mejorar performance',
      'Practica combinando LIMIT con diferentes ORDER BY para ver diferentes "tops"'
    ]
  },
  {
    id: 4,
    level: 'NIVEL PRE-BÁSICO',
    title: 'Ejercicio 4: Productos con Buen Margen',
    description: 'Encontrar productos donde la diferencia entre precio de venta y costo es mayor a $50, calculando la ganancia.',
    expectedColumns: ['nombre_producto', 'precio_unitario', 'costo_unitario', 'ganancia'],
    solution: `SELECT 
    nombre_producto,
    precio_unitario,
    costo_unitario,
    (precio_unitario - costo_unitario) AS ganancia
FROM productos
WHERE (precio_unitario - costo_unitario) > 50
ORDER BY ganancia DESC;`,
    difficulty: 'pre-basic',
    why: {
      title: '¿Por qué usar expresiones y alias (AS) aquí?',
      content: `Las **expresiones aritméticas** y los **alias (AS)** te permiten calcular valores derivados y darles nombres descriptivos. En lugar de solo mostrar datos almacenados, puedes crear nuevas columnas calculadas.

En este ejercicio, usamos **expresiones** para calcular la ganancia (precio - costo) y el **alias AS ganancia** para darle un nombre claro a esta columna calculada. Esto hace que el resultado sea más legible y útil.

### ¿Cuándo usar expresiones y alias?
- Cuando necesitas **calcular** valores derivados de columnas existentes
- Cuando quieres **renombrar** columnas para que sean más descriptivas
- Cuando necesitas **transformar** datos antes de mostrarlos
- Cuando quieres hacer **análisis** matemático sobre los datos

### En el mundo real:
Cuando un analista financiero necesita calcular el margen de ganancia de cada producto, usa expresiones como (precio - costo) AS ganancia. Sin esto, tendría que calcular manualmente después de exportar los datos.`,
      keyPoints: [
        'Las expresiones pueden usar operadores aritméticos: +, -, *, /, %',
        'AS crea un alias (nombre alternativo) para una columna o expresión',
        'Puedes usar el alias en ORDER BY y WHERE (en algunos casos)',
        'Las expresiones calculan valores sobre la marcha, no se almacenan en la base de datos'
      ],
      realWorldExample: 'Cuando un gerente de producto necesita ver qué productos tienen mejor margen de ganancia, calcula (precio_venta - costo) AS margen y ordena por ese valor para identificar oportunidades.'
    },
    theory: `# Expresiones Aritméticas y Alias (AS)

## ¿Qué son las Expresiones?

Las **expresiones** en SQL te permiten realizar cálculos matemáticos sobre los datos. Puedes sumar, restar, multiplicar, dividir y más.

### Operadores Aritméticos:
- **+**: Suma
- **-**: Resta
- **\***: Multiplicación
- **/**: División
- **%**: Módulo (resto de la división)

### Ejemplo Básico:
\`\`\`sql
SELECT 
    precio_unitario,
    costo_unitario,
    (precio_unitario - costo_unitario) AS ganancia
FROM productos;
\`\`\`

## ¿Qué es AS (Alias)?

**AS** te permite dar un nombre alternativo (alias) a una columna o expresión. Esto hace que los resultados sean más legibles y descriptivos.

### Sintaxis:
\`\`\`sql
SELECT 
    columna_original AS nombre_descriptivo,
    (expresion) AS nombre_calculado
FROM tabla;
\`\`\`

### Ejemplo:
\`\`\`sql
SELECT 
    nombre_producto,
    precio_unitario AS precio,
    costo_unitario AS costo,
    (precio_unitario - costo_unitario) AS ganancia
FROM productos;
\`\`\`

## Expresiones Complejas

Puedes crear expresiones más complejas combinando múltiples operaciones:

\`\`\`sql
SELECT 
    nombre_producto,
    precio_unitario,
    cantidad_stock,
    (precio_unitario * cantidad_stock) AS valor_inventario,
    ((precio_unitario - costo_unitario) / precio_unitario * 100) AS porcentaje_margen
FROM productos;
\`\`\`

## Usando Alias en ORDER BY

Una vez que defines un alias, puedes usarlo en ORDER BY:

\`\`\`sql
SELECT 
    nombre_producto,
    precio_unitario,
    costo_unitario,
    (precio_unitario - costo_unitario) AS ganancia
FROM productos
ORDER BY ganancia DESC;  -- Usa el alias aquí
\`\`\`

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    nombre_producto,
    precio_unitario,
    costo_unitario,
    (precio_unitario - costo_unitario) AS ganancia
FROM productos
WHERE (precio_unitario - costo_unitario) > 50
ORDER BY ganancia DESC;
\`\`\`

Este query:
1. Selecciona columnas originales y una calculada
2. **Calcula** la ganancia usando una expresión aritmética
3. **Nombra** la expresión calculada como "ganancia" usando AS
4. **Filtra** productos con ganancia mayor a $50
5. **Ordena** por ganancia descendente

## Precedencia de Operadores

SQL sigue las reglas matemáticas estándar:
1. Paréntesis: ( )
2. Multiplicación y División: *, /
3. Suma y Resta: +, -

\`\`\`sql
-- Esto calcula: (a * b) + c
SELECT (precio * cantidad) + descuento AS total;

-- Esto calcula: a * (b + c)
SELECT precio * (cantidad + descuento) AS total;
\`\`\`

## Casos de Uso Comunes

### Cálculo de Totales
\`\`\`sql
SELECT 
    producto,
    cantidad,
    precio_unitario,
    (cantidad * precio_unitario) AS total_linea
FROM ventas;
\`\`\`

### Cálculo de Porcentajes
\`\`\`sql
SELECT 
    producto,
    ventas_actuales,
    ventas_anterior,
    ((ventas_actuales - ventas_anterior) / ventas_anterior * 100) AS crecimiento_porcentual
FROM estadisticas;
\`\`\`

### Renombrado de Columnas
\`\`\`sql
SELECT 
    nombre AS nombre_completo,
    fecha_ingreso AS fecha_contratacion,
    salario_base AS salario_mensual
FROM empleados;
\`\`\`

## Ventajas de Expresiones y Alias

- **Cálculos en tiempo real**: No necesitas procesar datos fuera de SQL
- **Legibilidad**: Los alias hacen los resultados más claros
- **Eficiencia**: El servidor calcula, no tu aplicación
- **Flexibilidad**: Puedes crear cualquier métrica derivada que necesites`,
    tips: [
      'Usa paréntesis para controlar el orden de las operaciones',
      'Los alias hacen los resultados más legibles, úsalos siempre que sea posible',
      'Puedes usar el alias en ORDER BY pero no siempre en WHERE (depende del motor SQL)',
      'Las expresiones se calculan sobre la marcha, no se almacenan en la base de datos'
    ],
    advice: [
      'Practica creando diferentes expresiones aritméticas',
      'Usa alias descriptivos que expliquen qué representa el valor calculado',
      'Combina expresiones con WHERE para filtrar por valores calculados',
      'Experimenta con diferentes operadores para ver qué puedes calcular'
    ]
  },
  // NIVEL BÁSICO 1
  {
    id: 5,
    level: 'NIVEL BÁSICO 1',
    title: 'Ejercicio 5: Estadísticas Generales de Ventas',
    description: 'Calcular estadísticas generales: total de transacciones, vendedores activos, productos distintos vendidos y unidades totales.',
    expectedColumns: ['total_transacciones', 'vendedores_activos', 'productos_vendidos', 'unidades_totales_vendidas'],
    solution: `SELECT 
    COUNT(*) AS total_transacciones,
    COUNT(DISTINCT id_vendedor) AS vendedores_activos,
    COUNT(DISTINCT id_producto) AS productos_vendidos,
    SUM(cantidad) AS unidades_totales_vendidas
FROM ventas;`,
    difficulty: 'basic-1',
    why: {
      title: '¿Por qué usar funciones de agregación (COUNT, SUM) aquí?',
      content: `Las **funciones de agregación** te permiten calcular estadísticas y resúmenes sobre múltiples filas de datos. En lugar de contar manualmente, SQL puede procesar miles de registros en milisegundos.

En este ejercicio, usamos **COUNT** para contar transacciones y vendedores únicos, y **SUM** para sumar todas las unidades vendidas. Estas funciones procesan toda la tabla y devuelven un solo valor resumen.

### ¿Cuándo usar funciones de agregación?
- Cuando necesitas **contar** registros o valores únicos
- Cuando quieres **sumar** valores numéricos
- Cuando necesitas calcular **promedios**, **mínimos** o **máximos**
- Cuando quieres **resumir** grandes volúmenes de datos en métricas útiles

### En el mundo real:
Cuando un gerente necesita un dashboard con estadísticas generales de ventas (total de transacciones, vendedores activos, productos vendidos), usa funciones de agregación. Sin estas funciones, tendría que procesar manualmente miles de registros.`,
      keyPoints: [
        'COUNT(*) cuenta todas las filas, COUNT(columna) cuenta valores no nulos',
        'COUNT(DISTINCT columna) cuenta valores únicos, eliminando duplicados',
        'SUM suma valores numéricos de una columna',
        'Las funciones de agregación procesan múltiples filas y devuelven un solo valor'
      ],
      realWorldExample: 'Cuando un director de ventas necesita un reporte ejecutivo con estadísticas generales del mes, usa COUNT y SUM para obtener métricas agregadas de todas las ventas.'
    },
    theory: `# Funciones de Agregación: COUNT y SUM

## ¿Qué son las Funciones de Agregación?

Las **funciones de agregación** procesan múltiples filas y devuelven un solo valor resumen. Son esenciales para análisis y reportes.

### Funciones Principales:
- **COUNT**: Cuenta filas o valores
- **SUM**: Suma valores numéricos
- **AVG**: Calcula el promedio
- **MIN**: Encuentra el valor mínimo
- **MAX**: Encuentra el valor máximo

## COUNT: Contando Registros

### COUNT(*)
Cuenta todas las filas de la tabla:
\`\`\`sql
SELECT COUNT(*) FROM ventas;
-- Devuelve: número total de filas
\`\`\`

### COUNT(columna)
Cuenta filas donde la columna NO es NULL:
\`\`\`sql
SELECT COUNT(id_vendedor) FROM ventas;
-- Devuelve: número de ventas con vendedor asignado
\`\`\`

### COUNT(DISTINCT columna)
Cuenta valores únicos (elimina duplicados):
\`\`\`sql
SELECT COUNT(DISTINCT id_vendedor) FROM ventas;
-- Devuelve: número de vendedores únicos que hicieron ventas
\`\`\`

## SUM: Sumando Valores

**SUM** suma todos los valores numéricos de una columna:
\`\`\`sql
SELECT SUM(cantidad) FROM ventas;
-- Devuelve: suma total de todas las cantidades
\`\`\`

### Características:
- Ignora valores NULL
- Solo funciona con tipos numéricos
- Puede sumar expresiones: \`SUM(cantidad * precio)\`

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    COUNT(*) AS total_transacciones,
    COUNT(DISTINCT id_vendedor) AS vendedores_activos,
    COUNT(DISTINCT id_producto) AS productos_vendidos,
    SUM(cantidad) AS unidades_totales_vendidas
FROM ventas;
\`\`\`

Este query:
1. **COUNT(\*)**: Cuenta todas las filas (transacciones totales)
2. **COUNT(DISTINCT id_vendedor)**: Cuenta vendedores únicos
3. **COUNT(DISTINCT id_producto)**: Cuenta productos únicos vendidos
4. **SUM(cantidad)**: Suma todas las unidades vendidas

## Diferencia: COUNT(*) vs COUNT(columna)

- **COUNT(\*)**: Cuenta todas las filas, incluso si tienen NULLs
- **COUNT(columna)**: Cuenta solo filas donde columna NO es NULL
- **COUNT(DISTINCT columna)**: Cuenta valores únicos no nulos

\`\`\`sql
-- Ejemplo:
-- Tabla con 5 filas, 2 tienen NULL en columna_x
SELECT COUNT(*) FROM tabla;           -- Devuelve: 5
SELECT COUNT(columna_x) FROM tabla;   -- Devuelve: 3
SELECT COUNT(DISTINCT columna_x) FROM tabla; -- Devuelve: valores únicos
\`\`\`

## Casos de Uso Comunes

### Estadísticas Generales
\`\`\`sql
SELECT 
    COUNT(*) AS total_registros,
    SUM(monto) AS total_ingresos,
    AVG(monto) AS promedio_ingresos
FROM transacciones;
\`\`\`

### Conteo de Valores Únicos
\`\`\`sql
SELECT 
    COUNT(DISTINCT cliente_id) AS clientes_unicos,
    COUNT(DISTINCT producto_id) AS productos_unicos
FROM ventas;
\`\`\`

## Ventajas de las Funciones de Agregación

- **Eficiencia**: Procesan miles de registros en milisegundos
- **Precisión**: Evitan errores de cálculo manual
- **Performance**: Optimizadas por el motor de base de datos
- **Simplicidad**: Una línea de código reemplaza cientos de operaciones`,
    tips: [
      'COUNT(*) es más rápido que COUNT(columna) porque no necesita verificar NULLs',
      'COUNT(DISTINCT) es útil para contar valores únicos sin duplicados',
      'SUM ignora valores NULL automáticamente',
      'Las funciones de agregación siempre devuelven un solo valor, incluso si la tabla está vacía'
    ],
    advice: [
      'Usa COUNT(*) cuando quieras contar todas las filas',
      'Usa COUNT(DISTINCT) cuando necesites valores únicos',
      'Combina múltiples funciones de agregación para reportes completos',
      'Practica con diferentes columnas para entender cómo cada función se comporta'
    ]
  },
  {
    id: 6,
    level: 'NIVEL BÁSICO 1',
    title: 'Ejercicio 6: Resumen por Categoría de Productos',
    description: 'Agrupar productos por categoría y calcular: cantidad de productos, precio promedio, mínimo, máximo y margen promedio.',
    expectedColumns: ['categoria', 'cantidad_productos', 'precio_promedio', 'precio_minimo', 'precio_maximo', 'margen_promedio'],
    solution: `SELECT 
    categoria,
    COUNT(*) AS cantidad_productos,
    AVG(precio_unitario) AS precio_promedio,
    MIN(precio_unitario) AS precio_minimo,
    MAX(precio_unitario) AS precio_maximo,
    AVG(precio_unitario - costo_unitario) AS margen_promedio
FROM productos
GROUP BY categoria
ORDER BY cantidad_productos DESC;`,
    difficulty: 'basic-1',
    why: {
      title: '¿Por qué usar GROUP BY aquí?',
      content: `**GROUP BY** te permite agrupar filas que tienen el mismo valor en una o más columnas y aplicar funciones de agregación a cada grupo. Es como crear "subconjuntos" de datos y calcular estadísticas para cada uno.

En este ejercicio, usamos **GROUP BY categoria** para agrupar productos por su categoría y luego calcular estadísticas (promedio, mínimo, máximo) para cada categoría por separado. Sin GROUP BY, solo obtendríamos estadísticas generales de todos los productos juntos.

### ¿Cuándo usar GROUP BY?
- Cuando necesitas **agrupar** datos por categorías o segmentos
- Cuando quieres calcular estadísticas **por grupo** en lugar de generales
- Cuando necesitas **comparar** métricas entre diferentes grupos
- Cuando quieres crear **resúmenes** segmentados de tus datos

### En el mundo real:
Cuando un gerente de producto necesita comparar precios promedio entre diferentes categorías (Electrónicos vs Ropa vs Hogar), usa GROUP BY categoria para obtener métricas separadas por categoría.`,
      keyPoints: [
        'GROUP BY agrupa filas con el mismo valor en las columnas especificadas',
        'Todas las columnas en SELECT deben estar en GROUP BY o ser funciones de agregación',
        'GROUP BY permite calcular estadísticas por grupo en lugar de generales',
        'Puedes agrupar por múltiples columnas separadas por comas'
      ],
      realWorldExample: 'Cuando un analista necesita comparar ventas promedio por región, usa GROUP BY region para obtener métricas separadas para cada región.'
    },
    theory: `# GROUP BY: Agrupando y Resumiendo Datos

## ¿Qué es GROUP BY?

**GROUP BY** agrupa filas que tienen el mismo valor en una o más columnas y permite aplicar funciones de agregación a cada grupo por separado.

### Sintaxis Básica:
\`\`\`sql
SELECT columna_grupo, funcion_agregacion(columna)
FROM tabla
GROUP BY columna_grupo;
\`\`\`

## Cómo Funciona GROUP BY

GROUP BY divide los datos en grupos y luego aplica funciones de agregación a cada grupo:

\`\`\`sql
-- Sin GROUP BY: estadísticas generales
SELECT AVG(precio_unitario) FROM productos;
-- Devuelve: promedio de TODOS los productos

-- Con GROUP BY: estadísticas por grupo
SELECT categoria, AVG(precio_unitario) 
FROM productos 
GROUP BY categoria;
-- Devuelve: promedio por cada categoría
\`\`\`

## Regla de SELECT con GROUP BY

**Todas las columnas en SELECT deben:**
- Estar en GROUP BY, O
- Ser funciones de agregación (COUNT, SUM, AVG, etc.)

### ✅ Correcto:
\`\`\`sql
SELECT categoria, COUNT(*), AVG(precio)
FROM productos
GROUP BY categoria;
\`\`\`

### ❌ Incorrecto:
\`\`\`sql
SELECT categoria, nombre_producto, COUNT(*)  -- Error: nombre_producto no está en GROUP BY
FROM productos
GROUP BY categoria;
\`\`\`

## Funciones de Agregación con GROUP BY

### AVG: Promedio
\`\`\`sql
SELECT categoria, AVG(precio_unitario) AS precio_promedio
FROM productos
GROUP BY categoria;
\`\`\`

### MIN y MAX: Valores Extremos
\`\`\`sql
SELECT categoria, 
       MIN(precio_unitario) AS precio_minimo,
       MAX(precio_unitario) AS precio_maximo
FROM productos
GROUP BY categoria;
\`\`\`

### COUNT: Conteo por Grupo
\`\`\`sql
SELECT categoria, COUNT(*) AS cantidad_productos
FROM productos
GROUP BY categoria;
\`\`\`

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    categoria,
    COUNT(*) AS cantidad_productos,
    AVG(precio_unitario) AS precio_promedio,
    MIN(precio_unitario) AS precio_minimo,
    MAX(precio_unitario) AS precio_maximo,
    AVG(precio_unitario - costo_unitario) AS margen_promedio
FROM productos
GROUP BY categoria
ORDER BY cantidad_productos DESC;
\`\`\`

Este query:
1. **Agrupa** productos por categoría
2. **Cuenta** productos en cada categoría
3. **Calcula** estadísticas (promedio, min, max) por categoría
4. **Ordena** por cantidad de productos descendente

## Agrupación Múltiple

Puedes agrupar por múltiples columnas:
\`\`\`sql
SELECT departamento, categoria, COUNT(*)
FROM productos
GROUP BY departamento, categoria;
\`\`\`

Esto crea un grupo para cada combinación única de departamento + categoria.

## Orden de Ejecución

SQL ejecuta las cláusulas en este orden:
1. **FROM**: Identifica la tabla
2. **WHERE**: Filtra filas (si existe)
3. **GROUP BY**: Agrupa las filas
4. **SELECT**: Selecciona columnas y aplica funciones de agregación
5. **ORDER BY**: Ordena los resultados

## Casos de Uso Comunes

### Estadísticas por Categoría
\`\`\`sql
SELECT categoria, 
       COUNT(*) AS total,
       AVG(precio) AS precio_promedio
FROM productos
GROUP BY categoria;
\`\`\`

### Ventas por Vendedor
\`\`\`sql
SELECT id_vendedor, 
       COUNT(*) AS total_ventas,
       SUM(monto) AS total_vendido
FROM ventas
GROUP BY id_vendedor;
\`\`\`

## Ventajas de GROUP BY

- **Segmentación**: Analiza datos por grupos específicos
- **Comparación**: Compara métricas entre diferentes grupos
- **Resumen**: Crea reportes resumidos por categorías
- **Eficiencia**: Procesa y agrupa en una sola consulta`,
    tips: [
      'Todas las columnas no agregadas en SELECT deben estar en GROUP BY',
      'GROUP BY se ejecuta antes de SELECT, por eso puedes usar alias en ORDER BY pero no siempre en SELECT',
      'Puedes agrupar por múltiples columnas separadas por comas',
      'ORDER BY puede usar alias de funciones de agregación'
    ],
    advice: [
      'Siempre incluye la columna de agrupación en SELECT para ver qué grupo representa cada fila',
      'Usa GROUP BY cuando necesites comparar métricas entre diferentes categorías',
      'Combina GROUP BY con ORDER BY para ordenar los grupos por sus estadísticas',
      'Practica agrupando por diferentes columnas para entender cómo se comportan los datos'
    ]
  },
  {
    id: 7,
    level: 'NIVEL BÁSICO 1',
    title: 'Ejercicio 7: Departamentos con Masa Crítica',
    description: 'Identificar departamentos que tienen más de 2 vendedores, mostrando cantidad de vendedores y estadísticas salariales.',
    expectedColumns: ['departamento', 'cantidad_vendedores', 'salario_promedio', 'costo_total_departamento'],
    solution: `SELECT 
    departamento,
    COUNT(*) AS cantidad_vendedores,
    AVG(salario_base) AS salario_promedio,
    SUM(salario_base) AS costo_total_departamento
FROM vendedores
GROUP BY departamento
HAVING COUNT(*) > 2
ORDER BY cantidad_vendedores DESC;`,
    difficulty: 'basic-1',
    why: {
      title: '¿Por qué usar HAVING aquí?',
      content: `**HAVING** es como WHERE, pero para filtrar grupos después de que GROUP BY los ha creado. WHERE filtra filas individuales ANTES de agrupar, mientras que HAVING filtra grupos DESPUÉS de agrupar.

En este ejercicio, usamos **HAVING COUNT(*) > 2** para filtrar solo los departamentos que tienen más de 2 vendedores. No podemos usar WHERE aquí porque estamos filtrando por el resultado de una función de agregación (COUNT), no por una columna individual.

### ¿Cuándo usar HAVING?
- Cuando necesitas **filtrar grupos** basado en funciones de agregación
- Cuando quieres mostrar solo grupos que cumplen ciertas condiciones estadísticas
- Cuando necesitas filtrar DESPUÉS de agrupar, no antes
- Cuando la condición involucra COUNT, SUM, AVG, etc.

### En el mundo real:
Cuando un gerente necesita ver solo departamentos con más de 5 empleados para una reunión de recursos humanos, usa HAVING COUNT(*) > 5. WHERE no funcionaría porque necesita filtrar por el resultado del conteo, no por una columna individual.`,
      keyPoints: [
        'HAVING filtra grupos después de GROUP BY, WHERE filtra filas antes',
        'HAVING puede usar funciones de agregación en sus condiciones',
        'WHERE no puede usar funciones de agregación directamente',
        'Puedes usar tanto WHERE como HAVING en la misma consulta'
      ],
      realWorldExample: 'Cuando un analista necesita encontrar categorías de productos que vendieron más de 100 unidades, usa GROUP BY categoria HAVING SUM(cantidad) > 100.'
    },
    theory: `# HAVING: Filtrando Grupos

## ¿Qué es HAVING?

**HAVING** es una cláusula que filtra grupos después de que GROUP BY los ha creado. Es similar a WHERE, pero opera sobre grupos en lugar de filas individuales.

### Sintaxis:
\`\`\`sql
SELECT columna, funcion_agregacion(columna)
FROM tabla
GROUP BY columna
HAVING condicion_con_funcion_agregacion;
\`\`\`

## Diferencia: WHERE vs HAVING

### WHERE: Filtra Filas ANTES de Agrupar
\`\`\`sql
SELECT departamento, COUNT(*)
FROM vendedores
WHERE salario_base > 3000  -- Filtra filas individuales
GROUP BY departamento;
\`\`\`
Esto agrupa solo los vendedores con salario > 3000.

### HAVING: Filtra Grupos DESPUÉS de Agrupar
\`\`\`sql
SELECT departamento, COUNT(*)
FROM vendedores
GROUP BY departamento
HAVING COUNT(*) > 2;  -- Filtra grupos
\`\`\`
Esto muestra solo departamentos con más de 2 vendedores.

## Cuándo Usar Cada Una

### Usa WHERE cuando:
- Filtras por valores de columnas individuales
- La condición NO involucra funciones de agregación
- Quieres reducir el conjunto de datos antes de agrupar

### Usa HAVING cuando:
- Filtras por resultados de funciones de agregación
- La condición involucra COUNT, SUM, AVG, etc.
- Quieres filtrar grupos después de crearlos

## Combinando WHERE y HAVING

Puedes usar ambas en la misma consulta:

\`\`\`sql
SELECT departamento, COUNT(*), AVG(salario_base)
FROM vendedores
WHERE fecha_ingreso > '2020-01-01'  -- Filtra filas antes
GROUP BY departamento
HAVING COUNT(*) > 2;  -- Filtra grupos después
\`\`\`

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    departamento,
    COUNT(*) AS cantidad_vendedores,
    AVG(salario_base) AS salario_promedio,
    SUM(salario_base) AS costo_total_departamento
FROM vendedores
GROUP BY departamento
HAVING COUNT(*) > 2
ORDER BY cantidad_vendedores DESC;
\`\`\`

Este query:
1. **Agrupa** vendedores por departamento
2. **Calcula** estadísticas por departamento
3. **Filtra** solo departamentos con más de 2 vendedores (HAVING)
4. **Ordena** por cantidad de vendedores descendente

## Condiciones Comunes en HAVING

### Por Cantidad
\`\`\`sql
HAVING COUNT(*) > 10
HAVING COUNT(*) BETWEEN 5 AND 20
\`\`\`

### Por Suma
\`\`\`sql
HAVING SUM(monto) > 10000
HAVING SUM(cantidad) >= 100
\`\`\`

### Por Promedio
\`\`\`sql
HAVING AVG(precio) > 500
HAVING AVG(salario) BETWEEN 2000 AND 3000
\`\`\`

## Orden de Ejecución Completo

SQL ejecuta las cláusulas en este orden:
1. **FROM**: Identifica la tabla
2. **WHERE**: Filtra filas individuales
3. **GROUP BY**: Agrupa las filas
4. **HAVING**: Filtra los grupos
5. **SELECT**: Selecciona columnas
6. **ORDER BY**: Ordena los resultados

## Casos de Uso Comunes

### Top Departamentos por Ventas
\`\`\`sql
SELECT departamento, SUM(ventas) AS total
FROM ventas
GROUP BY departamento
HAVING SUM(ventas) > 50000
ORDER BY total DESC;
\`\`\`

### Categorías con Múltiples Productos
\`\`\`sql
SELECT categoria, COUNT(*) AS cantidad
FROM productos
GROUP BY categoria
HAVING COUNT(*) >= 5;
\`\`\`

## Ventajas de HAVING

- **Filtrado Inteligente**: Filtra basado en estadísticas, no solo valores individuales
- **Performance**: Reduce el número de grupos antes de procesarlos
- **Flexibilidad**: Permite condiciones complejas sobre agregaciones
- **Claridad**: Separa filtrado de filas (WHERE) vs filtrado de grupos (HAVING)`,
    tips: [
      'HAVING siempre va después de GROUP BY',
      'Puedes usar funciones de agregación en HAVING (COUNT, SUM, AVG, etc.)',
      'WHERE filtra antes de agrupar, HAVING filtra después',
      'Puedes usar alias de funciones de agregación en HAVING'
    ],
    advice: [
      'Usa HAVING cuando necesites filtrar por resultados de funciones de agregación',
      'Combina WHERE y HAVING para filtrar tanto filas como grupos',
      'Practica con diferentes condiciones en HAVING para entender cómo funciona',
      'Recuerda que HAVING opera sobre grupos, no sobre filas individuales'
    ]
  },
  // NIVEL BÁSICO 2
  {
    id: 8,
    level: 'NIVEL BÁSICO 2',
    title: 'Ejercicio 8: Clasificación de Productos por Precio',
    description: 'Clasificar productos como \'Premium\' (≥$500), \'Medio\' ($100-$499) o \'Económico\' (<$100).',
    expectedColumns: ['nombre_producto', 'precio_unitario', 'clasificacion_precio'],
    solution: `SELECT 
    nombre_producto,
    precio_unitario,
    CASE 
        WHEN precio_unitario >= 500 THEN 'Premium'
        WHEN precio_unitario >= 100 THEN 'Medio'
        ELSE 'Económico'
    END AS clasificacion_precio
FROM productos
ORDER BY precio_unitario DESC;`,
    difficulty: 'basic-2',
    why: {
      title: '¿Por qué usar CASE WHEN aquí?',
      content: `**CASE WHEN** es la forma de SQL de hacer lógica condicional (como if/else en otros lenguajes). Te permite crear categorías o clasificaciones basadas en condiciones.

En este ejercicio, usamos **CASE WHEN** para clasificar productos en categorías de precio (Premium, Medio, Económico) basado en su precio_unitario. Sin CASE, tendrías que hacer esta clasificación manualmente después de obtener los datos.

### ¿Cuándo usar CASE WHEN?
- Cuando necesitas **clasificar** o **categorizar** datos según condiciones
- Cuando quieres crear **segmentos** o **rangos** de valores
- Cuando necesitas **transformar** valores según reglas de negocio
- Cuando quieres crear **columnas calculadas** con lógica condicional

### En el mundo real:
Cuando un analista necesita segmentar clientes por nivel de gasto (Alto, Medio, Bajo) o productos por categoría de precio, usa CASE WHEN. Esto permite análisis segmentado directamente en SQL sin procesamiento adicional.`,
      keyPoints: [
        'CASE WHEN evalúa condiciones en orden y devuelve el primer valor que coincide',
        'ELSE es opcional pero recomendado para manejar casos no contemplados',
        'Puedes anidar múltiples WHEN para múltiples condiciones',
        'CASE WHEN puede usarse en SELECT, WHERE, ORDER BY y otras cláusulas'
      ],
      realWorldExample: 'Cuando un gerente de marketing necesita segmentar clientes por nivel de compra (VIP si gastó >$1000, Regular si gastó $100-$1000, Nuevo si <$100), usa CASE WHEN para crear esta clasificación automáticamente.'
    },
    theory: `# CASE WHEN: Lógica Condicional en SQL

## ¿Qué es CASE WHEN?

**CASE WHEN** es la estructura de control condicional de SQL. Permite crear lógica tipo "si-entonces-sino" directamente en tus consultas.

### Sintaxis Básica:
\`\`\`sql
SELECT 
    columna,
    CASE 
        WHEN condicion1 THEN valor1
        WHEN condicion2 THEN valor2
        ELSE valor_por_defecto
    END AS nombre_columna
FROM tabla;
\`\`\`

## Cómo Funciona CASE WHEN

CASE evalúa condiciones en orden y devuelve el primer valor que coincide:

\`\`\`sql
CASE 
    WHEN precio >= 500 THEN 'Premium'
    WHEN precio >= 100 THEN 'Medio'
    ELSE 'Económico'
END
\`\`\`

- Si precio >= 500 → devuelve 'Premium'
- Si precio >= 100 (pero < 500) → devuelve 'Medio'
- Si precio < 100 → devuelve 'Económico'

## Estructura Completa

### CASE Simple (Compara un valor)
\`\`\`sql
CASE columna
    WHEN valor1 THEN resultado1
    WHEN valor2 THEN resultado2
    ELSE resultado_default
END
\`\`\`

### CASE con Condiciones (Más Flexible)
\`\`\`sql
CASE 
    WHEN condicion1 THEN resultado1
    WHEN condicion2 THEN resultado2
    ELSE resultado_default
END
\`\`\`

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    nombre_producto,
    precio_unitario,
    CASE 
        WHEN precio_unitario >= 500 THEN 'Premium'
        WHEN precio_unitario >= 100 THEN 'Medio'
        ELSE 'Económico'
    END AS clasificacion_precio
FROM productos
ORDER BY precio_unitario DESC;
\`\`\`

Este query:
1. Selecciona nombre y precio del producto
2. **Clasifica** cada producto según su precio usando CASE
3. Crea una nueva columna "clasificacion_precio"
4. Ordena por precio descendente

## Orden de Evaluación

**IMPORTANTE**: CASE evalúa condiciones de arriba hacia abajo y se detiene en la primera que es verdadera:

\`\`\`sql
-- Ejemplo: precio = 150
CASE 
    WHEN precio >= 500 THEN 'Premium'  -- Falso, continúa
    WHEN precio >= 100 THEN 'Medio'     -- Verdadero, devuelve 'Medio' y se detiene
    ELSE 'Económico'                    -- No se evalúa
END
\`\`\`

## Casos de Uso Comunes

### Clasificación por Rangos
\`\`\`sql
CASE 
    WHEN edad >= 65 THEN 'Senior'
    WHEN edad >= 18 THEN 'Adulto'
    ELSE 'Menor'
END AS categoria_edad
\`\`\`

### Transformación de Valores
\`\`\`sql
CASE 
    WHEN estado = 'A' THEN 'Activo'
    WHEN estado = 'I' THEN 'Inactivo'
    ELSE 'Desconocido'
END AS estado_descripcion
\`\`\`

### Cálculos Condicionales
\`\`\`sql
CASE 
    WHEN cantidad > 10 THEN precio * 0.9  -- 10% descuento
    WHEN cantidad > 5 THEN precio * 0.95   -- 5% descuento
    ELSE precio                            -- Sin descuento
END AS precio_final
\`\`\`

## Usando CASE en Diferentes Cláusulas

### En SELECT
\`\`\`sql
SELECT nombre, CASE WHEN salario > 3000 THEN 'Alto' ELSE 'Bajo' END
FROM empleados;
\`\`\`

### En WHERE
\`\`\`sql
SELECT * FROM productos
WHERE CASE WHEN precio > 100 THEN categoria = 'Premium' ELSE categoria = 'Económico' END;
\`\`\`

### En ORDER BY
\`\`\`sql
SELECT * FROM productos
ORDER BY CASE WHEN categoria = 'Premium' THEN 1 ELSE 2 END;
\`\`\`

## Ventajas de CASE WHEN

- **Lógica en SQL**: No necesitas procesar en tu aplicación
- **Eficiencia**: El servidor procesa la lógica
- **Claridad**: La lógica de negocio está visible en la consulta
- **Flexibilidad**: Puedes crear cualquier clasificación que necesites`,
    tips: [
      'CASE evalúa condiciones en orden, pon las más específicas primero',
      'Siempre incluye ELSE para manejar casos no contemplados',
      'Puedes usar CASE en SELECT, WHERE, ORDER BY y otras cláusulas',
      'CASE puede anidarse para lógica más compleja'
    ],
    advice: [
      'Usa CASE WHEN para crear categorías y segmentaciones directamente en SQL',
      'Practica con diferentes condiciones para entender el orden de evaluación',
      'Combina CASE con GROUP BY para análisis segmentado',
      'Considera usar CASE para transformar datos según reglas de negocio'
    ]
  },
  {
    id: 9,
    level: 'NIVEL BÁSICO 2',
    title: 'Ejercicio 9: Segmentación Salarial de Vendedores',
    description: 'Categorizar vendedores por salario: \'Alto\' (≥$3000), \'Medio\' ($2800-$2999) o \'Inicial\' (<$2800).',
    expectedColumns: ['nombre', 'apellido', 'departamento', 'salario_base', 'categoria_salarial'],
    solution: `SELECT 
    nombre,
    apellido,
    departamento,
    salario_base,
    CASE 
        WHEN salario_base >= 3000 THEN 'Alto'
        WHEN salario_base >= 2800 THEN 'Medio'
        ELSE 'Inicial'
    END AS categoria_salarial
FROM vendedores
ORDER BY salario_base DESC;`,
    difficulty: 'basic-2',
    why: {
      title: '¿Por qué usar CASE WHEN para segmentación?',
      content: `La **segmentación** es fundamental en análisis de datos. Te permite dividir tu audiencia o datos en grupos con características similares para análisis más profundo.

En este ejercicio, usamos **CASE WHEN** para segmentar vendedores por nivel salarial. Esta segmentación permite identificar patrones, comparar grupos y tomar decisiones estratégicas (por ejemplo, qué departamentos tienen más vendedores de alto nivel).

### ¿Por qué segmentar?
- **Análisis comparativo**: Comparar métricas entre diferentes segmentos
- **Estrategia**: Aplicar diferentes estrategias a diferentes segmentos
- **Identificación**: Identificar grupos con características específicas
- **Reportes**: Crear reportes más informativos y accionables

### En el mundo real:
Cuando RRHH necesita analizar la distribución salarial por departamento para planificar aumentos, usa CASE WHEN para segmentar y luego GROUP BY para comparar segmentos entre departamentos.`,
      keyPoints: [
        'La segmentación permite análisis más granular y específico',
        'CASE WHEN crea segmentos basados en condiciones de negocio',
        'Puedes combinar segmentación con GROUP BY para análisis comparativo',
        'Los segmentos deben ser mutuamente excluyentes y exhaustivos'
      ],
      realWorldExample: 'Cuando un gerente necesita entender qué porcentaje de clientes son "VIP" vs "Regular" vs "Nuevo", usa CASE WHEN para segmentar y luego COUNT con GROUP BY para ver la distribución.'
    },
    theory: `# Segmentación con CASE WHEN

## ¿Qué es la Segmentación?

La **segmentación** divide datos en grupos con características similares. CASE WHEN es la herramienta perfecta para crear estos segmentos en SQL.

### Ejemplo de Segmentación:
\`\`\`sql
SELECT 
    nombre,
    salario_base,
    CASE 
        WHEN salario_base >= 3000 THEN 'Alto'
        WHEN salario_base >= 2800 THEN 'Medio'
        ELSE 'Inicial'
    END AS categoria_salarial
FROM vendedores;
\`\`\`

## Creando Segmentos Efectivos

### Reglas para Buena Segmentación:
1. **Mutualmente Excluyentes**: Cada registro pertenece a un solo segmento
2. **Exhaustivos**: Todos los registros tienen un segmento (usa ELSE)
3. **Relevantes**: Los segmentos deben tener significado de negocio
4. **Accionables**: Debes poder tomar decisiones basadas en los segmentos

### Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    nombre,
    apellido,
    departamento,
    salario_base,
    CASE 
        WHEN salario_base >= 3000 THEN 'Alto'
        WHEN salario_base >= 2800 THEN 'Medio'
        ELSE 'Inicial'
    END AS categoria_salarial
FROM vendedores
ORDER BY salario_base DESC;
\`\`\`

Este query:
1. Selecciona información del vendedor
2. **Segmenta** cada vendedor según su salario
3. Crea la columna "categoria_salarial"
4. Ordena por salario descendente

## Combinando Segmentación con GROUP BY

Una vez que segmentas, puedes analizar cada segmento:

\`\`\`sql
SELECT 
    CASE 
        WHEN salario_base >= 3000 THEN 'Alto'
        WHEN salario_base >= 2800 THEN 'Medio'
        ELSE 'Inicial'
    END AS categoria_salarial,
    COUNT(*) AS cantidad_vendedores,
    AVG(salario_base) AS salario_promedio
FROM vendedores
GROUP BY categoria_salarial;
\`\`\`

## Casos de Uso de Segmentación

### Segmentación de Clientes
\`\`\`sql
CASE 
    WHEN total_compras > 1000 THEN 'VIP'
    WHEN total_compras > 500 THEN 'Regular'
    ELSE 'Nuevo'
END AS segmento_cliente
\`\`\`

### Segmentación de Productos
\`\`\`sql
CASE 
    WHEN precio >= 500 THEN 'Premium'
    WHEN precio >= 100 THEN 'Medio'
    ELSE 'Económico'
END AS segmento_precio
\`\`\`

### Segmentación Temporal
\`\`\`sql
CASE 
    WHEN antiguedad > 24 THEN 'Senior'
    WHEN antiguedad >= 12 THEN 'Intermedio'
    ELSE 'Junior'
END AS segmento_experiencia
\`\`\`

## Ventajas de Segmentación en SQL

- **Análisis Directo**: Segmenta y analiza en una sola consulta
- **Eficiencia**: No necesitas procesar datos fuera de SQL
- **Consistencia**: Misma lógica de segmentación en todos los reportes
- **Flexibilidad**: Puedes crear cualquier segmentación que necesites`,
    tips: [
      'Asegúrate de que los segmentos sean mutuamente excluyentes',
      'Siempre incluye ELSE para cubrir todos los casos',
      'Combina segmentación con GROUP BY para análisis comparativo',
      'Usa nombres descriptivos para los segmentos que reflejen su propósito'
    ],
    advice: [
      'Define segmentos que tengan significado de negocio real',
      'Practica combinando CASE con GROUP BY para análisis segmentado',
      'Considera usar segmentación para crear dashboards más informativos',
      'Documenta los criterios de segmentación para mantener consistencia'
    ]
  },
  {
    id: 10,
    level: 'NIVEL BÁSICO 2',
    title: 'Ejercicio 10: Análisis de Antigüedad con CASE',
    description: 'Calcular antigüedad en meses y clasificar: \'Senior\' (>24 meses), \'Intermedio\' (12-24 meses) o \'Junior\' (<12 meses).',
    expectedColumns: ['nombre', 'apellido', 'fecha_ingreso', 'meses_antiguedad', 'nivel_experiencia'],
    solution: `SELECT 
    nombre,
    apellido,
    fecha_ingreso,
    TIMESTAMPDIFF(MONTH, fecha_ingreso, CURDATE()) AS meses_antiguedad,
    CASE 
        WHEN TIMESTAMPDIFF(MONTH, fecha_ingreso, CURDATE()) > 24 THEN 'Senior'
        WHEN TIMESTAMPDIFF(MONTH, fecha_ingreso, CURDATE()) >= 12 THEN 'Intermedio'
        ELSE 'Junior'
    END AS nivel_experiencia
FROM vendedores
ORDER BY meses_antiguedad DESC;`,
    difficulty: 'basic-2',
    why: {
      title: '¿Por qué usar funciones de fecha (TIMESTAMPDIFF) aquí?',
      content: `Las **funciones de fecha** te permiten calcular diferencias de tiempo, extraer partes de fechas y realizar operaciones temporales directamente en SQL. Son esenciales para análisis basados en tiempo.

En este ejercicio, usamos **TIMESTAMPDIFF** para calcular la antigüedad en meses desde la fecha de ingreso hasta hoy. Esto nos permite clasificar empleados por nivel de experiencia basado en tiempo real, no en datos estáticos.

### ¿Cuándo usar funciones de fecha?
- Cuando necesitas calcular **diferencias** entre fechas (edad, antigüedad, duración)
- Cuando quieres **filtrar** por períodos de tiempo (último mes, último año)
- Cuando necesitas **extraer** partes de fechas (año, mes, día)
- Cuando quieres hacer **análisis temporal** de tus datos

### En el mundo real:
Cuando RRHH necesita identificar empleados elegibles para promociones basadas en antigüedad, usa TIMESTAMPDIFF para calcular años de servicio y luego CASE para clasificarlos.`,
      keyPoints: [
        'TIMESTAMPDIFF calcula diferencias entre dos fechas en la unidad especificada',
        'CURDATE() devuelve la fecha actual',
        'Puedes calcular diferencias en años, meses, días, horas, etc.',
        'Las funciones de fecha son específicas del motor SQL (MySQL usa TIMESTAMPDIFF)'
      ],
      realWorldExample: 'Cuando un gerente necesita identificar productos que no se han vendido en los últimos 3 meses, usa TIMESTAMPDIFF(MONTH, ultima_venta, CURDATE()) > 3.'
    },
    theory: `# Funciones de Fecha: TIMESTAMPDIFF y CURDATE

## Funciones de Fecha en MySQL

SQL proporciona funciones poderosas para trabajar con fechas y tiempos. Estas funciones son esenciales para análisis temporal.

### TIMESTAMPDIFF: Calculando Diferencias

**TIMESTAMPDIFF** calcula la diferencia entre dos fechas en la unidad especificada.

### Sintaxis:
\`\`\`sql
TIMESTAMPDIFF(UNIDAD, fecha_inicio, fecha_fin)
\`\`\`

### Unidades Disponibles:
- **YEAR**: Años
- **MONTH**: Meses
- **DAY**: Días
- **HOUR**: Horas
- **MINUTE**: Minutos
- **SECOND**: Segundos

### Ejemplos:
\`\`\`sql
-- Diferencia en meses
TIMESTAMPDIFF(MONTH, '2020-01-15', '2023-06-20')
-- Devuelve: 41 meses

-- Diferencia en años
TIMESTAMPDIFF(YEAR, fecha_nacimiento, CURDATE())
-- Devuelve: edad en años

-- Diferencia en días
TIMESTAMPDIFF(DAY, fecha_venta, CURDATE())
-- Devuelve: días desde la venta
\`\`\`

## CURDATE(): Fecha Actual

**CURDATE()** devuelve la fecha actual del sistema:

\`\`\`sql
SELECT CURDATE();
-- Devuelve: 2024-01-15 (ejemplo)
\`\`\`

### Otras Funciones de Fecha Útiles:
- **NOW()**: Fecha y hora actual
- **DATE_FORMAT()**: Formatear fechas
- **DATE_ADD()**: Sumar tiempo a una fecha
- **DATE_SUB()**: Restar tiempo de una fecha

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    nombre,
    apellido,
    fecha_ingreso,
    TIMESTAMPDIFF(MONTH, fecha_ingreso, CURDATE()) AS meses_antiguedad,
    CASE 
        WHEN TIMESTAMPDIFF(MONTH, fecha_ingreso, CURDATE()) > 24 THEN 'Senior'
        WHEN TIMESTAMPDIFF(MONTH, fecha_ingreso, CURDATE()) >= 12 THEN 'Intermedio'
        ELSE 'Junior'
    END AS nivel_experiencia
FROM vendedores
ORDER BY meses_antiguedad DESC;
\`\`\`

Este query:
1. Selecciona información del vendedor
2. **Calcula** antigüedad en meses usando TIMESTAMPDIFF
3. **Clasifica** por nivel de experiencia usando CASE
4. Ordena por antigüedad descendente

## Combinando Funciones de Fecha con CASE

Puedes usar funciones de fecha directamente en CASE:

\`\`\`sql
CASE 
    WHEN TIMESTAMPDIFF(YEAR, fecha_ingreso, CURDATE()) > 5 THEN 'Veterano'
    WHEN TIMESTAMPDIFF(YEAR, fecha_ingreso, CURDATE()) >= 2 THEN 'Experto'
    ELSE 'Nuevo'
END AS categoria_antiguedad
\`\`\`

## Casos de Uso Comunes

### Cálculo de Edad
\`\`\`sql
SELECT nombre, 
       TIMESTAMPDIFF(YEAR, fecha_nacimiento, CURDATE()) AS edad
FROM personas;
\`\`\`

### Productos Recientes
\`\`\`sql
SELECT * FROM productos
WHERE TIMESTAMPDIFF(DAY, fecha_creacion, CURDATE()) <= 30;
\`\`\`

### Análisis de Retención
\`\`\`sql
SELECT 
    TIMESTAMPDIFF(MONTH, fecha_registro, CURDATE()) AS meses_registrado,
    COUNT(*) AS cantidad_clientes
FROM clientes
GROUP BY meses_registrado;
\`\`\`

## Ventajas de Funciones de Fecha

- **Cálculos Automáticos**: Calcula diferencias automáticamente
- **Precisión**: Maneja años bisiestos y meses con diferentes días
- **Eficiencia**: Procesa en el servidor, no en tu aplicación
- **Flexibilidad**: Múltiples unidades de tiempo disponibles`,
    tips: [
      'TIMESTAMPDIFF siempre resta fecha_inicio de fecha_fin',
      'CURDATE() devuelve solo la fecha, NOW() devuelve fecha y hora',
      'Puedes usar funciones de fecha en WHERE para filtrar por tiempo',
      'Las funciones de fecha son específicas del motor SQL (MySQL vs PostgreSQL vs SQL Server)'
    ],
    advice: [
      'Usa TIMESTAMPDIFF para calcular edades, antigüedades y duraciones',
      'Combina funciones de fecha con CASE para clasificaciones temporales',
      'Practica con diferentes unidades (YEAR, MONTH, DAY) para entender las diferencias',
      'Considera usar funciones de fecha para análisis de tendencias temporales'
    ]
  },
  // NIVEL BÁSICO 3
  {
    id: 11,
    level: 'NIVEL BÁSICO 3',
    title: 'Ejercicio 11: Productos Más Caros que el Promedio',
    description: 'Identificar productos cuyo precio supera el precio promedio general, mostrando ambos valores para comparación.',
    expectedColumns: ['nombre_producto', 'categoria', 'precio_unitario', 'precio_promedio'],
    solution: `WITH precio_avg AS (
    SELECT AVG(precio_unitario) AS precio_promedio
    FROM productos
)
SELECT 
    p.nombre_producto,
    p.categoria,
    p.precio_unitario,
    pa.precio_promedio
FROM productos p
CROSS JOIN precio_avg pa
WHERE p.precio_unitario > pa.precio_promedio
ORDER BY p.precio_unitario DESC;`,
    difficulty: 'basic-3',
    why: {
      title: '¿Por qué usar CTE (WITH) aquí?',
      content: `**CTE (Common Table Expression)** con **WITH** te permite crear una "tabla temporal" dentro de tu consulta. Es como definir una subconsulta con nombre que puedes reutilizar. Hace las consultas más legibles y organizadas.

En este ejercicio, usamos **WITH precio_avg AS** para calcular el precio promedio una vez y luego compararlo con cada producto. Sin CTE, tendrías que repetir el cálculo del promedio en cada comparación o usar subconsultas menos legibles.

### ¿Cuándo usar CTE?
- Cuando necesitas **reutilizar** un cálculo o subconsulta múltiples veces
- Cuando quieres hacer consultas más **legibles** y organizadas
- Cuando necesitas crear **pasos intermedios** en análisis complejos
- Cuando quieres **modularizar** consultas grandes en partes más pequeñas

### En el mundo real:
Cuando un analista necesita comparar cada producto contra promedios o estadísticas generales, usa CTE para calcular las estadísticas una vez y luego compararlas eficientemente.`,
      keyPoints: [
        'CTE se define con WITH nombre AS (consulta)',
        'CTE existe solo durante la ejecución de la consulta',
        'Puedes tener múltiples CTEs separados por comas',
        'CTE mejora legibilidad y permite reutilización de cálculos'
      ],
      realWorldExample: 'Cuando un gerente necesita comparar ventas de cada vendedor contra el promedio del departamento, usa CTE para calcular el promedio una vez y luego compararlo con cada vendedor.'
    },
    theory: `# CTE (Common Table Expression): Consultas con WITH

## ¿Qué es un CTE?

**CTE (Common Table Expression)** es una tabla temporal que existe solo durante la ejecución de una consulta. Se define con la cláusula **WITH** y hace las consultas más legibles y organizadas.

### Sintaxis Básica:
\`\`\`sql
WITH nombre_cte AS (
    SELECT ...
)
SELECT ... FROM nombre_cte;
\`\`\`

## Ventajas de CTE

### 1. Legibilidad
Las consultas complejas se vuelven más fáciles de leer:
\`\`\`sql
-- Sin CTE: difícil de leer
SELECT * FROM (
    SELECT AVG(precio) AS promedio FROM productos
) AS avg_table;

-- Con CTE: más claro
WITH precio_promedio AS (
    SELECT AVG(precio) AS promedio FROM productos
)
SELECT * FROM precio_promedio;
\`\`\`

### 2. Reutilización
Puedes usar el CTE múltiples veces en la misma consulta.

### 3. Modularización
Divide consultas complejas en pasos más simples.

## Ejemplo Completo del Ejercicio

\`\`\`sql
WITH precio_avg AS (
    SELECT AVG(precio_unitario) AS precio_promedio
    FROM productos
)
SELECT 
    p.nombre_producto,
    p.categoria,
    p.precio_unitario,
    pa.precio_promedio
FROM productos p
CROSS JOIN precio_avg pa
WHERE p.precio_unitario > pa.precio_promedio
ORDER BY p.precio_unitario DESC;
\`\`\`

Este query:
1. **Crea** un CTE que calcula el precio promedio
2. **Combina** productos con el promedio usando CROSS JOIN
3. **Filtra** productos más caros que el promedio
4. **Ordena** por precio descendente

## CROSS JOIN

**CROSS JOIN** combina cada fila de la primera tabla con cada fila de la segunda tabla. En este caso, combina cada producto con el valor promedio (una sola fila).

## Múltiples CTEs

Puedes definir múltiples CTEs separados por comas:
\`\`\`sql
WITH cte1 AS (SELECT ...),
     cte2 AS (SELECT ...)
SELECT ... FROM cte1 JOIN cte2 ...;
\`\`\`

## Casos de Uso Comunes

### Comparación con Promedios
\`\`\`sql
WITH promedio AS (SELECT AVG(valor) AS avg FROM tabla)
SELECT * FROM tabla t
CROSS JOIN promedio p
WHERE t.valor > p.avg;
\`\`\`

### Cálculos Intermedios
\`\`\`sql
WITH ventas_por_vendedor AS (
    SELECT id_vendedor, SUM(monto) AS total
    FROM ventas GROUP BY id_vendedor
)
SELECT * FROM ventas_por_vendedor WHERE total > 1000;
\`\`\`

## Ventajas de CTE

- **Legibilidad**: Consultas más claras y organizadas
- **Reutilización**: Usa el mismo cálculo múltiples veces
- **Mantenibilidad**: Más fácil de modificar y depurar
- **Modularización**: Divide problemas complejos en partes simples`,
    tips: [
      'CTE se ejecuta solo una vez, incluso si se referencia múltiples veces',
      'Puedes usar CTE en SELECT, FROM, JOIN, etc.',
      'CTE mejora legibilidad especialmente en consultas complejas',
      'Puedes anidar CTEs (un CTE puede usar otro CTE)'
    ],
    advice: [
      'Usa CTE cuando necesites reutilizar cálculos o subconsultas',
      'CTE es especialmente útil para comparaciones con promedios o estadísticas',
      'Considera usar CTE para hacer consultas complejas más legibles',
      'Practica creando múltiples CTEs para análisis paso a paso'
    ]
  },
  {
    id: 12,
    level: 'NIVEL BÁSICO 3',
    title: 'Ejercicio 12: Vendedores con Salario Superior al Promedio de su Departamento',
    description: 'Encontrar vendedores que ganan más que el promedio de su propio departamento.',
    expectedColumns: ['nombre', 'apellido', 'departamento', 'salario_base', 'salario_promedio_dept'],
    solution: `WITH salario_dept AS (
    SELECT 
        departamento,
        AVG(salario_base) AS salario_promedio_dept
    FROM vendedores
    GROUP BY departamento
)
SELECT 
    v.nombre,
    v.apellido,
    v.departamento,
    v.salario_base,
    sd.salario_promedio_dept
FROM vendedores v
INNER JOIN salario_dept sd ON v.departamento = sd.departamento
WHERE v.salario_base > sd.salario_promedio_dept
ORDER BY v.departamento, v.salario_base DESC;`,
    difficulty: 'basic-3',
    why: {
      title: '¿Por qué usar INNER JOIN con CTE aquí?',
      content: `**INNER JOIN** combina filas de dos tablas cuando hay una coincidencia en la condición especificada. En este ejercicio, combinamos vendedores con los promedios de su departamento usando **INNER JOIN** sobre el departamento.

Usamos **INNER JOIN** porque queremos solo vendedores que tienen un departamento con promedio calculado. Si un departamento no tiene vendedores o no se puede calcular el promedio, no aparecerá en los resultados.

### ¿Cuándo usar INNER JOIN?
- Cuando necesitas **combinar** datos de múltiples tablas
- Cuando solo quieres filas que tienen **coincidencias** en ambas tablas
- Cuando necesitas **relacionar** datos basado en una condición
- Cuando quieres **filtrar** resultados a solo aquellos con relaciones válidas

### En el mundo real:
Cuando RRHH necesita comparar salarios individuales contra promedios departamentales, usa INNER JOIN para combinar cada vendedor con el promedio de su departamento y luego filtrar los que están por encima del promedio.`,
      keyPoints: [
        'INNER JOIN solo devuelve filas con coincidencias en ambas tablas',
        'La condición ON especifica cómo se relacionan las tablas',
        'INNER JOIN es el tipo de JOIN más común',
        'Puedes usar alias de tabla para hacer consultas más legibles'
      ],
      realWorldExample: 'Cuando un gerente necesita identificar empleados que ganan más que el promedio de su departamento para análisis de compensación, usa INNER JOIN para combinar datos de empleados con estadísticas departamentales.'
    },
    theory: `# INNER JOIN: Combinando Tablas con Coincidencias

## ¿Qué es INNER JOIN?

**INNER JOIN** combina filas de dos tablas cuando hay una coincidencia en la condición especificada. Solo devuelve filas que tienen coincidencias en ambas tablas.

### Sintaxis Básica:
\`\`\`sql
SELECT columnas
FROM tabla1 t1
INNER JOIN tabla2 t2 ON t1.columna = t2.columna;
\`\`\`

## Cómo Funciona INNER JOIN

INNER JOIN compara cada fila de la primera tabla con cada fila de la segunda tabla y devuelve solo las que cumplen la condición:

\`\`\`sql
-- Ejemplo simple
SELECT v.nombre, v.departamento, d.nombre_departamento
FROM vendedores v
INNER JOIN departamentos d ON v.departamento = d.id;
\`\`\`

## Ejemplo Completo del Ejercicio

\`\`\`sql
WITH salario_dept AS (
    SELECT departamento, AVG(salario_base) AS salario_promedio_dept
    FROM vendedores GROUP BY departamento
)
SELECT 
    v.nombre, v.apellido, v.departamento,
    v.salario_base, sd.salario_promedio_dept
FROM vendedores v
INNER JOIN salario_dept sd ON v.departamento = sd.departamento
WHERE v.salario_base > sd.salario_promedio_dept
ORDER BY v.departamento, v.salario_base DESC;
\`\`\`

Este query:
1. **Crea** un CTE con promedios por departamento
2. **Combina** vendedores con promedios usando INNER JOIN
3. **Filtra** vendedores con salario mayor al promedio
4. **Ordena** por departamento y salario

## Alias de Tabla

Los **alias** hacen las consultas más legibles:
\`\`\`sql
FROM vendedores v  -- 'v' es el alias
INNER JOIN departamentos d ON v.departamento = d.id;
\`\`\`

## Diferencia: INNER JOIN vs LEFT JOIN

- **INNER JOIN**: Solo filas con coincidencias en ambas tablas
- **LEFT JOIN**: Todas las filas de la tabla izquierda, incluso sin coincidencias

## Casos de Uso Comunes

### Combinar Tablas Relacionadas
\`\`\`sql
SELECT v.nombre, p.nombre_producto
FROM ventas v
INNER JOIN productos p ON v.id_producto = p.id;
\`\`\`

### Múltiples JOINs
\`\`\`sql
SELECT v.nombre, p.nombre_producto, c.nombre_cliente
FROM ventas v
INNER JOIN productos p ON v.id_producto = p.id
INNER JOIN clientes c ON v.id_cliente = c.id;
\`\`\`

## Ventajas de INNER JOIN

- **Relaciones**: Combina datos relacionados de múltiples tablas
- **Eficiencia**: Solo procesa filas con coincidencias
- **Claridad**: Hace explícitas las relaciones entre tablas
- **Flexibilidad**: Puedes combinar múltiples tablas`,
    tips: [
      'INNER JOIN solo devuelve filas con coincidencias en ambas tablas',
      'Usa alias de tabla para hacer consultas más legibles',
      'La condición ON especifica cómo se relacionan las tablas',
      'Puedes usar múltiples INNER JOINs en la misma consulta'
    ],
    advice: [
      'Usa INNER JOIN cuando necesites combinar datos relacionados',
      'Asegúrate de que la condición ON sea correcta para obtener los resultados esperados',
      'Combina INNER JOIN con CTE para consultas más complejas y legibles',
      'Practica con diferentes tipos de JOINs para entender cuándo usar cada uno'
    ]
  },
  {
    id: 13,
    level: 'NIVEL BÁSICO 3',
    title: 'Ejercicio 13: Productos Nunca Vendidos',
    description: 'Identificar qué productos no tienen ningún registro en la tabla de ventas.',
    expectedColumns: ['id_producto', 'nombre_producto', 'categoria', 'precio_unitario'],
    solution: `SELECT 
    p.id_producto,
    p.nombre_producto,
    p.categoria,
    p.precio_unitario
FROM productos p
LEFT JOIN ventas v ON p.id_producto = v.id_producto
WHERE v.id_producto IS NULL
ORDER BY p.categoria, p.nombre_producto;`,
    difficulty: 'basic-3',
    why: {
      title: '¿Por qué usar LEFT JOIN con IS NULL aquí?',
      content: `**LEFT JOIN** devuelve todas las filas de la tabla izquierda, incluso si no hay coincidencias en la tabla derecha. Cuando no hay coincidencia, las columnas de la tabla derecha son NULL.

En este ejercicio, usamos **LEFT JOIN** para incluir TODOS los productos, incluso los que nunca se vendieron. Luego filtramos con **WHERE v.id_producto IS NULL** para encontrar solo los productos sin ventas.

### ¿Cuándo usar LEFT JOIN con IS NULL?
- Cuando necesitas encontrar registros **sin relaciones** (productos sin ventas, clientes sin pedidos)
- Cuando quieres incluir **todos** los registros de una tabla, incluso sin coincidencias
- Cuando necesitas identificar **valores faltantes** o relaciones ausentes
- Cuando quieres hacer análisis de **completitud** o **cobertura**

### En el mundo real:
Cuando un gerente de inventario necesita identificar productos que nunca se vendieron para decidir si discontinuarlos, usa LEFT JOIN con IS NULL para encontrar estos productos.`,
      keyPoints: [
        'LEFT JOIN devuelve todas las filas de la tabla izquierda',
        'Cuando no hay coincidencia, las columnas de la tabla derecha son NULL',
        'WHERE columna IS NULL encuentra filas sin coincidencias',
        'LEFT JOIN es útil para encontrar valores faltantes o relaciones ausentes'
      ],
      realWorldExample: 'Cuando un gerente necesita identificar clientes que nunca compraron nada para campañas de reactivación, usa LEFT JOIN clientes con ventas WHERE id_venta IS NULL.'
    },
    theory: `# LEFT JOIN: Incluyendo Todas las Filas de la Tabla Izquierda

## ¿Qué es LEFT JOIN?

**LEFT JOIN** devuelve todas las filas de la tabla izquierda y las filas coincidentes de la tabla derecha. Si no hay coincidencia, las columnas de la tabla derecha son NULL.

### Sintaxis Básica:
\`\`\`sql
SELECT columnas
FROM tabla_izquierda t1
LEFT JOIN tabla_derecha t2 ON t1.columna = t2.columna;
\`\`\`

## Cómo Funciona LEFT JOIN

LEFT JOIN incluye TODAS las filas de la tabla izquierda:
- Si hay coincidencia: incluye datos de ambas tablas
- Si NO hay coincidencia: incluye datos de la tabla izquierda y NULL para la derecha

## Encontrar Valores Sin Relaciones

Para encontrar filas sin coincidencias, usa **WHERE columna IS NULL**:

\`\`\`sql
SELECT p.*
FROM productos p
LEFT JOIN ventas v ON p.id = v.id_producto
WHERE v.id_producto IS NULL;
\`\`\`

Esto devuelve productos que nunca se vendieron.

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    p.id_producto,
    p.nombre_producto,
    p.categoria,
    p.precio_unitario
FROM productos p
LEFT JOIN ventas v ON p.id_producto = v.id_producto
WHERE v.id_producto IS NULL
ORDER BY p.categoria, p.nombre_producto;
\`\`\`

Este query:
1. **Selecciona** todos los productos (tabla izquierda)
2. **Combina** con ventas usando LEFT JOIN
3. **Filtra** productos sin ventas (WHERE v.id_producto IS NULL)
4. **Ordena** por categoría y nombre

## Diferencia: LEFT JOIN vs INNER JOIN

### INNER JOIN
Solo filas con coincidencias en ambas tablas:
\`\`\`sql
SELECT * FROM productos p
INNER JOIN ventas v ON p.id = v.id_producto;
-- Solo productos que se vendieron
\`\`\`

### LEFT JOIN
Todas las filas de productos, incluso sin ventas:
\`\`\`sql
SELECT * FROM productos p
LEFT JOIN ventas v ON p.id = v.id_producto;
-- Todos los productos, con NULL si no se vendieron
\`\`\`

## Casos de Uso Comunes

### Encontrar Registros Sin Relaciones
\`\`\`sql
-- Clientes sin pedidos
SELECT c.* FROM clientes c
LEFT JOIN pedidos p ON c.id = p.id_cliente
WHERE p.id_cliente IS NULL;
\`\`\`

### Análisis de Completitud
\`\`\`sql
-- Productos sin categoría asignada
SELECT p.* FROM productos p
LEFT JOIN categorias c ON p.categoria_id = c.id
WHERE c.id IS NULL;
\`\`\`

## Ventajas de LEFT JOIN

- **Inclusión Completa**: Incluye todas las filas de la tabla izquierda
- **Análisis de Faltantes**: Encuentra valores sin relaciones
- **Flexibilidad**: Útil para análisis de completitud y cobertura
- **NULL Handling**: Maneja valores faltantes explícitamente`,
    tips: [
      'LEFT JOIN devuelve todas las filas de la tabla izquierda',
      'Usa WHERE columna IS NULL para encontrar filas sin coincidencias',
      'Las columnas de la tabla derecha serán NULL cuando no hay coincidencia',
      'LEFT JOIN es útil para análisis de completitud y valores faltantes'
    ],
    advice: [
      'Usa LEFT JOIN cuando necesites incluir todas las filas de una tabla',
      'Combina LEFT JOIN con IS NULL para encontrar valores sin relaciones',
      'Practica identificando cuándo usar LEFT JOIN vs INNER JOIN',
      'Considera LEFT JOIN para análisis de completitud y cobertura de datos'
    ]
  },
  // NIVEL BÁSICO 4
  {
    id: 14,
    level: 'NIVEL BÁSICO 4',
    title: 'Ejercicio 14: Listado Simple de Ventas con Nombre del Vendedor',
    description: 'Mostrar ventas con información del vendedor que realizó cada transacción.',
    expectedColumns: ['id_venta', 'fecha_venta', 'nombre', 'apellido', 'cantidad', 'descuento_aplicado'],
    solution: `SELECT 
    v.id_venta,
    v.fecha_venta,
    vend.nombre,
    vend.apellido,
    v.cantidad,
    v.descuento_aplicado
FROM ventas v
INNER JOIN vendedores vend ON v.id_vendedor = vend.id_vendedor
ORDER BY v.fecha_venta DESC;`,
    difficulty: 'basic-4',
    why: {
      title: '¿Por qué combinar tablas con JOIN aquí?',
      content: `En bases de datos relacionales, los datos están **normalizados** en múltiples tablas para evitar duplicación. La tabla de ventas solo tiene el \`id_vendedor\`, no el nombre completo del vendedor.

Para mostrar información completa, necesitas **combinar** la tabla de ventas con la tabla de vendedores usando **JOIN**. Esto te permite obtener datos de múltiples tablas relacionadas en una sola consulta.

### ¿Por qué normalizar datos?
- **Evita duplicación**: No repites el nombre del vendedor en cada venta
- **Consistencia**: Si cambia el nombre, solo se actualiza en un lugar
- **Eficiencia**: Ahorra espacio y mejora performance
- **Integridad**: Mantiene relaciones válidas entre datos

### En el mundo real:
Cuando un gerente necesita ver un reporte de ventas con nombres de vendedores, usa JOIN para combinar la tabla de ventas (que tiene solo IDs) con la tabla de vendedores (que tiene nombres). Sin JOIN, solo vería números sin contexto.`,
      keyPoints: [
        'JOIN combina datos de múltiples tablas relacionadas',
        'Las tablas se relacionan mediante claves (id_vendedor)',
        'INNER JOIN solo muestra ventas con vendedores válidos',
        'JOIN es esencial para trabajar con bases de datos normalizadas'
      ],
      realWorldExample: 'Cuando un analista necesita un reporte de ventas con información completa del vendedor y producto, usa múltiples JOINs para combinar ventas, vendedores y productos en una sola consulta.'
    },
    theory: `# Combinando Tablas con JOIN

## ¿Por qué Combinar Tablas?

En bases de datos relacionales, los datos están **normalizados** en múltiples tablas. Esto significa que:
- La tabla \`ventas\` tiene solo \`id_vendedor\` (número)
- La tabla \`vendedores\` tiene \`nombre\`, \`apellido\`, etc.

Para mostrar información completa, necesitas combinar ambas tablas.

## INNER JOIN: Combinando Tablas Relacionadas

**INNER JOIN** combina filas de dos tablas cuando hay una coincidencia:

\`\`\`sql
SELECT ventas.*, vendedores.nombre, vendedores.apellido
FROM ventas
INNER JOIN vendedores ON ventas.id_vendedor = vendedores.id_vendedor;
\`\`\`

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    v.id_venta,
    v.fecha_venta,
    vend.nombre,
    vend.apellido,
    v.cantidad,
    v.descuento_aplicado
FROM ventas v
INNER JOIN vendedores vend ON v.id_vendedor = vend.id_vendedor
ORDER BY v.fecha_venta DESC;
\`\`\`

Este query:
1. **Selecciona** columnas de ambas tablas
2. **Combina** ventas con vendedores usando INNER JOIN
3. **Relaciona** mediante \`id_vendedor\`
4. **Ordena** por fecha descendente

## Alias de Tabla

Los **alias** (\`v\`, \`vend\`) hacen las consultas más legibles:
- \`v\` = tabla ventas
- \`vend\` = tabla vendedores

## Ventajas de JOIN

- **Información Completa**: Combina datos de múltiples tablas
- **Normalización**: Mantiene datos organizados sin duplicación
- **Eficiencia**: Una consulta obtiene todo lo necesario
- **Flexibilidad**: Puedes combinar múltiples tablas`,
    tips: [
      'Usa alias de tabla para hacer consultas más legibles',
      'INNER JOIN solo muestra filas con coincidencias en ambas tablas',
      'La condición ON especifica cómo se relacionan las tablas',
      'Puedes combinar múltiples tablas con varios JOINs'
    ],
    advice: [
      'Practica combinando diferentes tablas para entender las relaciones',
      'Usa alias descriptivos que indiquen qué representa cada tabla',
      'Asegúrate de que la condición ON sea correcta para obtener los resultados esperados',
      'Considera usar JOIN cuando necesites información de múltiples tablas relacionadas'
    ]
  },
  {
    id: 15,
    level: 'NIVEL BÁSICO 4',
    title: 'Ejercicio 15: Todos los Vendedores y sus Ventas (Incluyendo Sin Ventas)',
    description: 'Listar todos los vendedores y contar cuántas ventas ha realizado cada uno, incluyendo aquellos sin ventas.',
    expectedColumns: ['id_vendedor', 'nombre', 'apellido', 'departamento', 'total_ventas'],
    solution: `SELECT 
    vend.id_vendedor,
    vend.nombre,
    vend.apellido,
    vend.departamento,
    COUNT(v.id_venta) AS total_ventas
FROM vendedores vend
LEFT JOIN ventas v ON vend.id_vendedor = v.id_vendedor
GROUP BY vend.id_vendedor, vend.nombre, vend.apellido, vend.departamento
ORDER BY total_ventas DESC;`,
    difficulty: 'basic-4',
    why: {
      title: '¿Por qué usar LEFT JOIN con GROUP BY aquí?',
      content: `Este ejercicio necesita mostrar **TODOS** los vendedores, incluso los que no tienen ventas. Si usáramos INNER JOIN, solo veríamos vendedores con ventas.

**LEFT JOIN** incluye todas las filas de la tabla izquierda (vendedores), incluso si no hay coincidencias en la tabla derecha (ventas). Luego usamos **GROUP BY** para contar las ventas de cada vendedor, y **COUNT(v.id_venta)** devuelve 0 para vendedores sin ventas (porque COUNT ignora NULLs).

### ¿Por qué LEFT JOIN en lugar de INNER JOIN?
- **INNER JOIN**: Solo vendedores con ventas (excluye vendedores nuevos o sin actividad)
- **LEFT JOIN**: Todos los vendedores, con 0 ventas si no tienen ninguna
- **Análisis completo**: Permite identificar vendedores que necesitan apoyo

### En el mundo real:
Cuando un gerente necesita un reporte completo de todos los vendedores con sus ventas para identificar quién necesita capacitación o apoyo, usa LEFT JOIN para incluir todos los vendedores, incluso los que aún no tienen ventas.`,
      keyPoints: [
        'LEFT JOIN incluye todas las filas de la tabla izquierda',
        'COUNT(columna) cuenta solo valores no NULL, devolviendo 0 si no hay coincidencias',
        'GROUP BY agrupa por vendedor para contar sus ventas',
        'LEFT JOIN + GROUP BY es útil para análisis completos incluyendo ceros'
      ],
      realWorldExample: 'Cuando RRHH necesita evaluar el desempeño de todos los empleados, usa LEFT JOIN con GROUP BY para incluir incluso aquellos que aún no tienen métricas, mostrando 0 en lugar de excluirlos del reporte.'
    },
    theory: `# LEFT JOIN con GROUP BY: Análisis Completo Incluyendo Ceros

## Combinando LEFT JOIN y GROUP BY

Cuando necesitas mostrar **todos** los registros de una tabla y contar relaciones (incluso si son cero), combinas LEFT JOIN con GROUP BY.

## Cómo Funciona

### LEFT JOIN
Incluye todas las filas de la tabla izquierda:
\`\`\`sql
FROM vendedores vend
LEFT JOIN ventas v ON vend.id_vendedor = v.id_vendedor
\`\`\`
Esto incluye todos los vendedores, con NULL en columnas de ventas si no tienen ventas.

### GROUP BY
Agrupa por vendedor para contar:
\`\`\`sql
GROUP BY vend.id_vendedor, vend.nombre, vend.apellido, vend.departamento
\`\`\`

### COUNT con LEFT JOIN
\`\`\`sql
COUNT(v.id_venta) AS total_ventas
\`\`\`
- Si hay ventas: cuenta las ventas
- Si NO hay ventas: devuelve 0 (COUNT ignora NULLs)

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    vend.id_vendedor,
    vend.nombre,
    vend.apellido,
    vend.departamento,
    COUNT(v.id_venta) AS total_ventas
FROM vendedores vend
LEFT JOIN ventas v ON vend.id_vendedor = v.id_vendedor
GROUP BY vend.id_vendedor, vend.nombre, vend.apellido, vend.departamento
ORDER BY total_ventas DESC;
\`\`\`

Este query:
1. **Incluye** todos los vendedores (LEFT JOIN)
2. **Combina** con sus ventas (puede ser NULL)
3. **Agrupa** por vendedor
4. **Cuenta** ventas (0 si no tiene ninguna)
5. **Ordena** por total de ventas

## Diferencia: INNER JOIN vs LEFT JOIN con GROUP BY

### INNER JOIN
Solo vendedores con ventas:
\`\`\`sql
SELECT vend.nombre, COUNT(v.id_venta)
FROM vendedores vend
INNER JOIN ventas v ON vend.id_vendedor = v.id_vendedor
GROUP BY vend.id_vendedor;
-- Excluye vendedores sin ventas
\`\`\`

### LEFT JOIN
Todos los vendedores, con 0 si no tienen ventas:
\`\`\`sql
SELECT vend.nombre, COUNT(v.id_venta)
FROM vendedores vend
LEFT JOIN ventas v ON vend.id_vendedor = v.id_vendedor
GROUP BY vend.id_vendedor;
-- Incluye vendedores sin ventas (muestra 0)
\`\`\`

## Casos de Uso Comunes

### Análisis de Desempeño Completo
\`\`\`sql
SELECT empleado, COUNT(proyecto) AS proyectos_asignados
FROM empleados e
LEFT JOIN proyectos p ON e.id = p.id_empleado
GROUP BY empleado;
\`\`\`

### Reportes Inclusivos
\`\`\`sql
SELECT categoria, COUNT(producto) AS productos_vendidos
FROM categorias c
LEFT JOIN productos p ON c.id = p.categoria_id
GROUP BY categoria;
\`\`\`

## Ventajas

- **Análisis Completo**: Incluye todos los registros, incluso sin relaciones
- **Identificación de Problemas**: Encuentra registros sin actividad
- **Reportes Inclusivos**: Muestra el panorama completo, no solo lo activo`,
    tips: [
      'LEFT JOIN con GROUP BY incluye todos los registros de la tabla izquierda',
      'COUNT(columna) devuelve 0 cuando no hay coincidencias (ignora NULLs)',
      'Usa LEFT JOIN cuando necesites incluir registros sin relaciones',
      'Combina LEFT JOIN con GROUP BY para análisis completos con ceros'
    ],
    advice: [
      'Usa LEFT JOIN + GROUP BY cuando necesites análisis completos incluyendo ceros',
      'Practica identificando cuándo necesitas INNER JOIN vs LEFT JOIN',
      'Considera LEFT JOIN para reportes que deben incluir todos los registros',
      'Recuerda que COUNT ignora NULLs, devolviendo 0 cuando no hay coincidencias'
    ]
  },
  {
    id: 16,
    level: 'NIVEL BÁSICO 4',
    title: 'Ejercicio 16: Ventas Completas con Vendedor y Producto',
    description: 'Mostrar información completa de cada venta: vendedor, producto, cantidades y precio total calculado.',
    expectedColumns: ['id_venta', 'fecha_venta', 'nombre_vendedor', 'departamento', 'nombre_producto', 'categoria', 'cantidad', 'precio_unitario', 'precio_total'],
    solution: `SELECT 
    v.id_venta,
    v.fecha_venta,
    CONCAT(vend.nombre, ' ', vend.apellido) AS nombre_vendedor,
    vend.departamento,
    p.nombre_producto,
    p.categoria,
    v.cantidad,
    p.precio_unitario,
    (v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) AS precio_total
FROM ventas v
INNER JOIN vendedores vend ON v.id_vendedor = vend.id_vendedor
INNER JOIN productos p ON v.id_producto = p.id_producto
ORDER BY v.fecha_venta DESC;`,
    difficulty: 'basic-4',
    why: {
      title: '¿Por qué usar múltiples JOINs y CONCAT aquí?',
      content: `Este ejercicio necesita información de **tres tablas relacionadas**: ventas, vendedores y productos. Cada tabla tiene información parcial:
- **ventas**: tiene IDs de vendedor y producto, cantidad, descuento
- **vendedores**: tiene nombre y apellido (separados)
- **productos**: tiene nombre del producto, precio, categoría

Para crear un reporte completo, necesitas **múltiples JOINs** para combinar las tres tablas. Además, usas **CONCAT** para unir nombre y apellido en un solo campo más legible.

### ¿Por qué múltiples JOINs?
- **Normalización**: Cada tabla tiene su información específica
- **Relaciones**: Las tablas se relacionan mediante IDs
- **Información completa**: Necesitas datos de todas las tablas para el reporte
- **Una consulta**: Obtienes todo en una sola query en lugar de múltiples

### En el mundo real:
Cuando un gerente necesita un reporte detallado de ventas con información completa del vendedor y producto, usa múltiples JOINs para combinar todas las tablas relacionadas en una sola consulta eficiente.`,
      keyPoints: [
        'Puedes usar múltiples JOINs en una sola consulta',
        'CONCAT une múltiples valores de texto en uno solo',
        'Cada JOIN combina una tabla adicional con la consulta',
        'Múltiples JOINs permiten obtener información completa de varias tablas relacionadas'
      ],
      realWorldExample: 'Cuando un analista necesita un reporte completo de transacciones con información del cliente, producto y vendedor, usa múltiples JOINs para combinar todas las tablas relacionadas en una sola consulta.'
    },
    theory: `# Múltiples JOINs: Combinando Varias Tablas

## Combinando Múltiples Tablas

Puedes usar **múltiples JOINs** en una sola consulta para combinar varias tablas relacionadas:

\`\`\`sql
SELECT ...
FROM tabla1 t1
INNER JOIN tabla2 t2 ON t1.id = t2.id_tabla1
INNER JOIN tabla3 t3 ON t2.id = t3.id_tabla2;
\`\`\`

## CONCAT: Uniendo Texto

**CONCAT** une múltiples valores de texto en uno solo:

\`\`\`sql
CONCAT(nombre, ' ', apellido) AS nombre_completo
\`\`\`
Esto combina nombre, espacio y apellido en "Juan Pérez".

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    v.id_venta,
    v.fecha_venta,
    CONCAT(vend.nombre, ' ', vend.apellido) AS nombre_vendedor,
    vend.departamento,
    p.nombre_producto,
    p.categoria,
    v.cantidad,
    p.precio_unitario,
    (v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) AS precio_total
FROM ventas v
INNER JOIN vendedores vend ON v.id_vendedor = vend.id_vendedor
INNER JOIN productos p ON v.id_producto = p.id_producto
ORDER BY v.fecha_venta DESC;
\`\`\`

Este query:
1. **Combina** ventas con vendedores (primer JOIN)
2. **Combina** resultado con productos (segundo JOIN)
3. **Une** nombre y apellido con CONCAT
4. **Calcula** precio total con expresión
5. **Ordena** por fecha descendente

## Orden de JOINs

Los JOINs se ejecutan en orden:
1. Primero: \`ventas\` JOIN \`vendedores\`
2. Luego: resultado JOIN \`productos\`

## Expresiones Complejas

Puedes combinar expresiones aritméticas con JOINs:
\`\`\`sql
(v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) AS precio_total
\`\`\`
Esto calcula: cantidad × precio × (1 - descuento%)

## Casos de Uso Comunes

### Reportes Completos
\`\`\`sql
SELECT 
    CONCAT(c.nombre, ' ', c.apellido) AS cliente,
    p.nombre_producto,
    v.fecha_venta
FROM ventas v
INNER JOIN clientes c ON v.id_cliente = c.id
INNER JOIN productos p ON v.id_producto = p.id;
\`\`\`

## Ventajas de Múltiples JOINs

- **Información Completa**: Obtienes datos de todas las tablas relacionadas
- **Una Consulta**: Más eficiente que múltiples consultas separadas
- **Flexibilidad**: Puedes combinar tantas tablas como necesites
- **Expresiones**: Puedes calcular valores derivados en la misma consulta`,
    tips: [
      'Puedes usar múltiples JOINs en una sola consulta',
      'CONCAT une valores de texto, útil para nombres completos',
      'Cada JOIN combina una tabla adicional',
      'Usa alias de tabla para hacer múltiples JOINs más legibles'
    ],
    advice: [
      'Practica combinando múltiples tablas con varios JOINs',
      'Usa CONCAT para crear campos más legibles (nombres completos, direcciones)',
      'Asegúrate de que cada JOIN tenga la condición ON correcta',
      'Considera el orden de los JOINs para optimizar performance'
    ]
  },
  // NIVEL AVANZADO
  {
    id: 17,
    level: 'AVANZADO',
    title: 'Ejercicio 17: Ventas Totales por Vendedor con CTE',
    description: 'Usar un CTE para calcular ventas por vendedor y luego combinar con información completa del vendedor.',
    expectedColumns: ['nombre', 'apellido', 'departamento', 'total_ventas', 'monto_total'],
    solution: `WITH ventas_por_vendedor AS (
    SELECT 
        v.id_vendedor,
        COUNT(*) AS total_ventas,
        SUM(v.cantidad * p.precio_unitario) AS monto_total
    FROM ventas v
    INNER JOIN productos p ON v.id_producto = p.id_producto
    GROUP BY v.id_vendedor
)
SELECT 
    vend.nombre,
    vend.apellido,
    vend.departamento,
    COALESCE(vpv.total_ventas, 0) AS total_ventas,
    COALESCE(vpv.monto_total, 0) AS monto_total
FROM vendedores vend
LEFT JOIN ventas_por_vendedor vpv ON vend.id_vendedor = vpv.id_vendedor
ORDER BY monto_total DESC;`,
    difficulty: 'advanced',
    why: {
      title: '¿Por qué usar COALESCE con LEFT JOIN aquí?',
      content: `Cuando usas **LEFT JOIN**, las columnas de la tabla derecha pueden ser NULL si no hay coincidencias. **COALESCE** reemplaza valores NULL con un valor por defecto que especifiques.

En este ejercicio, usamos **COALESCE** para mostrar 0 en lugar de NULL cuando un vendedor no tiene ventas. Esto hace el reporte más legible y evita confusión con valores NULL.

### ¿Por qué COALESCE en lugar de dejar NULL?
- **Legibilidad**: 0 es más claro que NULL para indicar "sin ventas"
- **Cálculos**: Evita errores en operaciones matemáticas (NULL + número = NULL)
- **Reportes**: Los reportes se ven más profesionales con valores numéricos consistentes
- **UX**: Los usuarios entienden mejor 0 que NULL

### En el mundo real:
Cuando un gerente necesita un reporte de todos los vendedores con sus ventas, usa COALESCE para mostrar 0 en lugar de NULL, haciendo el reporte más claro y profesional.`,
      keyPoints: [
        'COALESCE devuelve el primer valor no NULL de una lista',
        'COALESCE(valor, 0) reemplaza NULL con 0',
        'Útil con LEFT JOIN cuando quieres valores por defecto en lugar de NULL',
        'COALESCE hace los reportes más legibles y profesionales'
      ],
      realWorldExample: 'Cuando un analista necesita un dashboard con métricas de todos los departamentos, usa COALESCE para mostrar 0 en lugar de NULL cuando un departamento no tiene métricas aún.'
    },
    theory: `# COALESCE: Manejo de Valores NULL

## ¿Qué es COALESCE?

**COALESCE** devuelve el primer valor no NULL de una lista de valores. Es útil para proporcionar valores por defecto cuando hay NULLs.

### Sintaxis:
\`\`\`sql
COALESCE(valor1, valor2, valor3, ...)
\`\`\`
Devuelve el primer valor que NO es NULL.

## Uso con LEFT JOIN

Cuando LEFT JOIN no encuentra coincidencias, devuelve NULL. COALESCE permite reemplazar esos NULLs:

\`\`\`sql
COALESCE(total_ventas, 0) AS total_ventas
\`\`\`
- Si total_ventas tiene valor: devuelve ese valor
- Si total_ventas es NULL: devuelve 0

## Ejemplo Completo del Ejercicio

\`\`\`sql
WITH ventas_por_vendedor AS (
    SELECT v.id_vendedor, COUNT(*) AS total_ventas, SUM(...) AS monto_total
    FROM ventas v INNER JOIN productos p ON v.id_producto = p.id_producto
    GROUP BY v.id_vendedor
)
SELECT 
    vend.nombre, vend.apellido, vend.departamento,
    COALESCE(vpv.total_ventas, 0) AS total_ventas,
    COALESCE(vpv.monto_total, 0) AS monto_total
FROM vendedores vend
LEFT JOIN ventas_por_vendedor vpv ON vend.id_vendedor = vpv.id_vendedor
ORDER BY monto_total DESC;
\`\`\`

Este query:
1. **Crea** CTE con ventas agrupadas por vendedor
2. **Combina** con LEFT JOIN (incluye todos los vendedores)
3. **Reemplaza** NULLs con 0 usando COALESCE
4. **Ordena** por monto total

## Ventajas de COALESCE

- **Valores por Defecto**: Proporciona valores cuando hay NULLs
- **Legibilidad**: Hace los resultados más claros
- **Cálculos**: Evita problemas con NULLs en operaciones matemáticas
- **Reportes**: Resultados más profesionales y consistentes`,
    tips: [
      'COALESCE devuelve el primer valor no NULL de la lista',
      'Útil con LEFT JOIN para reemplazar NULLs con valores por defecto',
      'COALESCE(valor, 0) es común para mostrar 0 en lugar de NULL',
      'Puedes usar múltiples valores: COALESCE(valor1, valor2, valor3)'
    ],
    advice: [
      'Usa COALESCE con LEFT JOIN cuando quieras valores por defecto en lugar de NULL',
      'Considera COALESCE para hacer reportes más legibles y profesionales',
      'Practica usando COALESCE con diferentes valores por defecto',
      'Recuerda que COALESCE evalúa valores de izquierda a derecha'
    ]
  },
  {
    id: 18,
    level: 'AVANZADO',
    title: 'Ejercicio 18: Comparación con Promedio usando CTE',
    description: 'Usar un CTE para calcular estadísticas generales y comparar cada producto contra esos promedios.',
    expectedColumns: ['nombre_producto', 'categoria', 'precio_unitario', 'precio_promedio', 'diferencia_precio', 'margen_porcentual', 'margen_promedio'],
    solution: `WITH estadisticas_producto AS (
    SELECT 
        AVG(precio_unitario) AS precio_promedio,
        AVG(precio_unitario - costo_unitario) AS margen_promedio
    FROM productos
)
SELECT 
    p.nombre_producto,
    p.categoria,
    p.precio_unitario,
    ep.precio_promedio,
    (p.precio_unitario - ep.precio_promedio) AS diferencia_precio,
    ((p.precio_unitario - p.costo_unitario) / p.precio_unitario * 100) AS margen_porcentual,
    ep.margen_promedio
FROM productos p
CROSS JOIN estadisticas_producto ep
WHERE p.precio_unitario > ep.precio_promedio
ORDER BY diferencia_precio DESC;`,
    difficulty: 'advanced',
    why: {
      title: '¿Por qué usar CROSS JOIN con CTE aquí?',
      content: `**CROSS JOIN** combina cada fila de la primera tabla con cada fila de la segunda tabla. En este caso, el CTE devuelve una sola fila con estadísticas (promedios), y CROSS JOIN combina cada producto con esas estadísticas.

Usamos **CROSS JOIN** porque queremos comparar cada producto individual contra las estadísticas generales. Esto crea un "producto cartesiano" donde cada producto se combina con las estadísticas, permitiendo comparaciones directas.

### ¿Por qué CROSS JOIN en lugar de subconsulta?
- **Legibilidad**: El CTE hace el código más claro que una subconsulta anidada
- **Reutilización**: Puedes usar las estadísticas múltiples veces en la consulta
- **Performance**: El CTE se calcula una vez y se reutiliza
- **Claridad**: Separa el cálculo de estadísticas del proceso de comparación

### En el mundo real:
Cuando un analista necesita comparar cada producto contra promedios del mercado para identificar productos premium o económicos, usa CROSS JOIN con un CTE que calcula los promedios una vez y luego compara cada producto.`,
      keyPoints: [
        'CROSS JOIN combina cada fila de la primera tabla con cada fila de la segunda',
        'Útil cuando la segunda tabla tiene una sola fila (como estadísticas)',
        'CTE + CROSS JOIN es más legible que subconsultas anidadas',
        'Permite comparaciones directas entre filas individuales y estadísticas agregadas'
      ],
      realWorldExample: 'Cuando un gerente necesita comparar el precio de cada producto contra el precio promedio del mercado para estrategia de pricing, usa CROSS JOIN con un CTE que calcula el promedio una vez.'
    },
    theory: `# CROSS JOIN: Combinación Cartesiana

## ¿Qué es CROSS JOIN?

**CROSS JOIN** combina cada fila de la primera tabla con cada fila de la segunda tabla, creando un producto cartesiano. Es útil cuando necesitas combinar cada registro con valores calculados o constantes.

### Sintaxis:
\`\`\`sql
SELECT ...
FROM tabla1
CROSS JOIN tabla2;
\`\`\`

## CROSS JOIN con CTE

Cuando el CTE devuelve una sola fila (como estadísticas), CROSS JOIN combina cada fila de la tabla principal con esa única fila:

\`\`\`sql
WITH estadisticas AS (SELECT AVG(precio) AS promedio FROM productos)
SELECT p.*, e.promedio
FROM productos p
CROSS JOIN estadisticas e;
\`\`\`

Esto combina cada producto con el promedio.

## Ejemplo Completo del Ejercicio

\`\`\`sql
WITH estadisticas_producto AS (
    SELECT 
        AVG(precio_unitario) AS precio_promedio,
        AVG(precio_unitario - costo_unitario) AS margen_promedio
    FROM productos
)
SELECT 
    p.nombre_producto, p.categoria, p.precio_unitario,
    ep.precio_promedio,
    (p.precio_unitario - ep.precio_promedio) AS diferencia_precio,
    ((p.precio_unitario - p.costo_unitario) / p.precio_unitario * 100) AS margen_porcentual,
    ep.margen_promedio
FROM productos p
CROSS JOIN estadisticas_producto ep
WHERE p.precio_unitario > ep.precio_promedio
ORDER BY diferencia_precio DESC;
\`\`\`

Este query:
1. **Calcula** estadísticas generales en el CTE
2. **Combina** cada producto con las estadísticas usando CROSS JOIN
3. **Compara** cada producto contra los promedios
4. **Filtra** productos más caros que el promedio
5. **Ordena** por diferencia de precio

## Cuándo Usar CROSS JOIN

- Cuando necesitas combinar cada fila con valores calculados
- Cuando la segunda tabla tiene una sola fila (estadísticas, constantes)
- Cuando quieres comparar cada registro contra promedios o totales
- Cuando necesitas crear combinaciones sistemáticas

## Ventajas de CROSS JOIN con CTE

- **Legibilidad**: Más claro que subconsultas anidadas
- **Eficiencia**: El CTE se calcula una vez
- **Flexibilidad**: Puedes usar las estadísticas múltiples veces
- **Claridad**: Separa cálculo de comparación`,
    tips: [
      'CROSS JOIN es útil cuando la segunda tabla tiene una sola fila',
      'Combina CROSS JOIN con CTE para comparaciones contra estadísticas',
      'CROSS JOIN crea producto cartesiano: cada fila de tabla1 × cada fila de tabla2',
      'Usa WHERE después de CROSS JOIN para filtrar resultados relevantes'
    ],
    advice: [
      'Usa CROSS JOIN cuando necesites comparar cada registro contra estadísticas agregadas',
      'Combina con CTE para hacer el código más legible y eficiente',
      'Practica identificando cuándo CROSS JOIN es más apropiado que otros JOINs',
      'Considera CROSS JOIN para análisis comparativos contra promedios o totales'
    ]
  },
  {
    id: 19,
    level: 'AVANZADO',
    title: 'Ejercicio 19: CTE Múltiple para Análisis Departamental',
    description: 'Usar dos CTEs para analizar ventas y costos por departamento, calculando beneficio neto.',
    expectedColumns: ['departamento', 'total_ventas', 'ingresos_totales', 'num_vendedores', 'costo_salarios', 'beneficio_neto', 'ingreso_per_capita'],
    solution: `WITH ventas_por_departamento AS (
    SELECT 
        vend.departamento,
        COUNT(v.id_venta) AS total_ventas,
        SUM(v.cantidad * p.precio_unitario) AS ingresos_totales
    FROM ventas v
    INNER JOIN vendedores vend ON v.id_vendedor = vend.id_vendedor
    INNER JOIN productos p ON v.id_producto = p.id_producto
    GROUP BY vend.departamento
),
costos_departamento AS (
    SELECT 
        departamento,
        COUNT(*) AS num_vendedores,
        SUM(salario_base) AS costo_salarios
    FROM vendedores
    GROUP BY departamento
)
SELECT 
    vd.departamento,
    vd.total_ventas,
    vd.ingresos_totales,
    cd.num_vendedores,
    cd.costo_salarios,
    (vd.ingresos_totales - cd.costo_salarios) AS beneficio_neto,
    (vd.ingresos_totales / cd.num_vendedores) AS ingreso_per_capita
FROM ventas_por_departamento vd
INNER JOIN costos_departamento cd ON vd.departamento = cd.departamento
ORDER BY beneficio_neto DESC;`,
    difficulty: 'advanced',
    why: {
      title: '¿Por qué usar múltiples CTEs aquí?',
      content: `Este ejercicio necesita calcular **dos métricas diferentes** por departamento: ingresos (de ventas) y costos (de salarios). Cada métrica requiere diferentes JOINs y agrupaciones.

En lugar de hacer una consulta compleja con múltiples subconsultas, usamos **múltiples CTEs** para calcular cada métrica por separado y luego combinarlas. Esto hace el código más legible, mantenible y fácil de entender.

### ¿Por qué múltiples CTEs en lugar de una consulta compleja?
- **Separación de responsabilidades**: Cada CTE calcula una métrica específica
- **Legibilidad**: Es más fácil entender qué hace cada CTE
- **Mantenibilidad**: Puedes modificar un CTE sin afectar el otro
- **Debugging**: Puedes ejecutar cada CTE por separado para verificar resultados
- **Reutilización**: Puedes usar los CTEs en otras consultas

### En el mundo real:
Cuando un CFO necesita un análisis de rentabilidad por departamento comparando ingresos y costos, usa múltiples CTEs para calcular cada métrica por separado y luego combinarlas para obtener el beneficio neto.`,
      keyPoints: [
        'Múltiples CTEs permiten calcular métricas diferentes por separado',
        'Cada CTE puede tener su propia lógica de JOINs y agrupaciones',
        'Los CTEs se combinan en el SELECT final usando JOIN',
        'Múltiples CTEs hacen el código más modular y mantenible'
      ],
      realWorldExample: 'Cuando un analista necesita comparar ingresos y gastos por región para análisis de rentabilidad, usa múltiples CTEs para calcular ingresos y gastos por separado, luego los combina para obtener el beneficio neto.'
    },
    theory: `# Múltiples CTEs: Análisis Modular Complejo

## ¿Qué son Múltiples CTEs?

Puedes definir **múltiples CTEs** en una sola consulta, separados por comas. Cada CTE puede calcular métricas diferentes y luego combinarse en el SELECT final.

### Sintaxis:
\`\`\`sql
WITH cte1 AS (SELECT ...),
     cte2 AS (SELECT ...)
SELECT ... FROM cte1 JOIN cte2 ...;
\`\`\`

## Ventajas de Múltiples CTEs

### 1. Separación de Responsabilidades
Cada CTE calcula una métrica específica:
- **CTE 1**: Calcula ingresos por departamento
- **CTE 2**: Calcula costos por departamento
- **SELECT final**: Combina ambos para beneficio neto

### 2. Legibilidad
Es más fácil entender qué hace cada parte:
\`\`\`sql
WITH ventas_por_departamento AS (...),  -- Ingresos
     costos_departamento AS (...)         -- Costos
SELECT ...                                -- Beneficio neto
\`\`\`

### 3. Mantenibilidad
Puedes modificar un CTE sin afectar el otro.

## Ejemplo Completo del Ejercicio

\`\`\`sql
WITH ventas_por_departamento AS (
    SELECT vend.departamento, COUNT(v.id_venta) AS total_ventas,
           SUM(v.cantidad * p.precio_unitario) AS ingresos_totales
    FROM ventas v
    INNER JOIN vendedores vend ON v.id_vendedor = vend.id_vendedor
    INNER JOIN productos p ON v.id_producto = p.id_producto
    GROUP BY vend.departamento
),
costos_departamento AS (
    SELECT departamento, COUNT(*) AS num_vendedores,
           SUM(salario_base) AS costo_salarios
    FROM vendedores
    GROUP BY departamento
)
SELECT 
    vd.departamento, vd.total_ventas, vd.ingresos_totales,
    cd.num_vendedores, cd.costo_salarios,
    (vd.ingresos_totales - cd.costo_salarios) AS beneficio_neto,
    (vd.ingresos_totales / cd.num_vendedores) AS ingreso_per_capita
FROM ventas_por_departamento vd
INNER JOIN costos_departamento cd ON vd.departamento = cd.departamento
ORDER BY beneficio_neto DESC;
\`\`\`

Este query:
1. **Primer CTE**: Calcula ingresos por departamento
2. **Segundo CTE**: Calcula costos por departamento
3. **SELECT**: Combina ambos CTEs y calcula beneficio neto
4. **Ordena** por beneficio neto descendente

## Combinando CTEs

Los CTEs se combinan usando JOINs normales:
\`\`\`sql
FROM cte1 t1
INNER JOIN cte2 t2 ON t1.columna = t2.columna;
\`\`\`

## Casos de Uso Comunes

### Análisis de Rentabilidad
\`\`\`sql
WITH ingresos AS (SELECT ...),
     costos AS (SELECT ...)
SELECT ingresos.total - costos.total AS beneficio
FROM ingresos JOIN costos ...;
\`\`\`

## Ventajas de Múltiples CTEs

- **Modularidad**: Cada CTE tiene una responsabilidad clara
- **Legibilidad**: Código más fácil de entender
- **Mantenibilidad**: Cambios aislados por CTE
- **Debugging**: Puedes ejecutar cada CTE por separado
- **Reutilización**: CTEs pueden usarse en otras consultas`,
    tips: [
      'Separa CTEs por comas cuando defines múltiples',
      'Cada CTE puede tener su propia lógica compleja',
      'Combina CTEs usando JOINs normales en el SELECT final',
      'Puedes ejecutar cada CTE por separado para debugging'
    ],
    advice: [
      'Usa múltiples CTEs cuando necesites calcular métricas diferentes por separado',
      'Separa responsabilidades: cada CTE debe tener un propósito claro',
      'Combina CTEs en el SELECT final para análisis complejos',
      'Practica creando CTEs modulares que puedas reutilizar'
    ]
  },
  // NIVEL AVANZADO
  {
    id: 20,
    level: 'AVANZADO',
    title: 'Ejercicio 20: Listado Unificado de Nombres',
    description: 'Combinar en una sola lista los nombres de vendedores y productos usando UNION.',
    expectedColumns: ['nombre_completo', 'tipo', 'info_adicional'],
    solution: `SELECT 
    nombre AS nombre_completo,
    'Vendedor' AS tipo,
    departamento AS info_adicional
FROM vendedores
UNION
SELECT 
    nombre_producto,
    'Producto',
    categoria
FROM productos
ORDER BY tipo, nombre_completo;`,
    difficulty: 'advanced',
    why: {
      title: '¿Por qué usar UNION aquí?',
      content: `**UNION** combina los resultados de dos o más consultas SELECT en una sola lista. Elimina duplicados automáticamente y requiere que todas las consultas tengan el mismo número y tipo de columnas.

En este ejercicio, usamos **UNION** para crear una lista unificada de nombres de vendedores y productos. Ambos tienen nombres, pero pertenecen a tablas diferentes. UNION permite combinarlos en una sola lista con una columna adicional que indica el tipo.

### ¿Por qué UNION en lugar de múltiples consultas?
- **Lista unificada**: Crea una sola lista combinada de diferentes fuentes
- **Estructura consistente**: Todas las filas tienen las mismas columnas
- **Eliminación de duplicados**: UNION automáticamente elimina filas duplicadas
- **Análisis consolidado**: Permite analizar diferentes tipos de entidades juntas

### En el mundo real:
Cuando un gerente necesita una lista unificada de todos los nombres (clientes, productos, proveedores) para búsqueda o análisis, usa UNION para combinar diferentes tablas en una sola lista estructurada.`,
      keyPoints: [
        'UNION combina resultados de múltiples SELECTs en una sola lista',
        'Todas las consultas deben tener el mismo número y tipo de columnas',
        'UNION elimina duplicados automáticamente',
        'Útil para crear listas unificadas de diferentes fuentes de datos'
      ],
      realWorldExample: 'Cuando un sistema necesita una búsqueda unificada que busque en múltiples tablas (clientes, productos, proveedores), usa UNION para combinar los resultados en una sola lista.'
    },
    theory: `# UNION: Combinando Resultados de Múltiples Consultas

## ¿Qué es UNION?

**UNION** combina los resultados de dos o más consultas SELECT en una sola lista. Todas las consultas deben tener el mismo número y tipo de columnas.

### Sintaxis Básica:
\`\`\`sql
SELECT columna1, columna2 FROM tabla1
UNION
SELECT columna1, columna2 FROM tabla2;
\`\`\`

## Reglas de UNION

### 1. Mismo Número de Columnas
Todas las consultas deben tener el mismo número de columnas:
\`\`\`sql
SELECT nombre, tipo FROM vendedores
UNION
SELECT nombre_producto, 'Producto' FROM productos;
\`\`\`

### 2. Tipos Compatibles
Las columnas deben ser del mismo tipo o compatible:
- Primera columna: texto en ambas consultas
- Segunda columna: texto en ambas consultas

### 3. Eliminación de Duplicados
UNION automáticamente elimina filas duplicadas.

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    nombre AS nombre_completo,
    'Vendedor' AS tipo,
    departamento AS info_adicional
FROM vendedores
UNION
SELECT 
    nombre_producto,
    'Producto',
    categoria
FROM productos
ORDER BY tipo, nombre_completo;
\`\`\`

Este query:
1. **Primera consulta**: Selecciona nombres de vendedores con tipo "Vendedor"
2. **Segunda consulta**: Selecciona nombres de productos con tipo "Producto"
3. **UNION**: Combina ambas listas en una sola
4. **ORDER BY**: Ordena por tipo y nombre

## UNION vs UNION ALL

### UNION
Elimina duplicados:
\`\`\`sql
SELECT nombre FROM tabla1
UNION
SELECT nombre FROM tabla2;
-- Elimina nombres duplicados
\`\`\`

### UNION ALL
Mantiene todos los registros, incluyendo duplicados:
\`\`\`sql
SELECT nombre FROM tabla1
UNION ALL
SELECT nombre FROM tabla2;
-- Incluye todos, incluso duplicados
\`\`\`

## Casos de Uso Comunes

### Listas Unificadas
\`\`\`sql
SELECT nombre, 'Cliente' AS tipo FROM clientes
UNION
SELECT nombre, 'Proveedor' AS tipo FROM proveedores;
\`\`\`

### Reportes Consolidados
\`\`\`sql
SELECT fecha, 'Venta' AS evento FROM ventas
UNION
SELECT fecha, 'Devolución' AS evento FROM devoluciones;
\`\`\`

## Ventajas de UNION

- **Listas Unificadas**: Combina diferentes fuentes en una lista
- **Estructura Consistente**: Todas las filas tienen las mismas columnas
- **Eliminación de Duplicados**: Automática con UNION
- **Flexibilidad**: Puedes combinar múltiples consultas`,
    tips: [
      'Todas las consultas en UNION deben tener el mismo número de columnas',
      'Las columnas deben ser del mismo tipo o compatible',
      'UNION elimina duplicados, UNION ALL los mantiene',
      'Usa alias consistentes en todas las consultas para claridad'
    ],
    advice: [
      'Usa UNION cuando necesites combinar resultados de diferentes tablas en una lista unificada',
      'Asegúrate de que todas las consultas tengan el mismo número y tipo de columnas',
      'Considera UNION ALL si quieres mantener duplicados para mejor performance',
      'Practica creando listas unificadas de diferentes fuentes de datos'
    ]
  },
  {
    id: 21,
    level: 'AVANZADO',
    title: 'Ejercicio 21: Análisis de Rangos de Precios y Salarios',
    description: 'Usar UNION ALL para crear un reporte consolidado de rangos de precios y salarios.',
    expectedColumns: ['categoria', 'cantidad', 'valor_promedio'],
    solution: `SELECT 
    CASE 
        WHEN precio_unitario >= 500 THEN 'Producto Premium'
        WHEN precio_unitario >= 100 THEN 'Producto Medio'
        ELSE 'Producto Económico'
    END AS categoria,
    COUNT(*) AS cantidad,
    AVG(precio_unitario) AS valor_promedio
FROM productos
GROUP BY CASE 
    WHEN precio_unitario >= 500 THEN 'Producto Premium'
    WHEN precio_unitario >= 100 THEN 'Producto Medio'
    ELSE 'Producto Económico'
END
UNION ALL
SELECT 
    CASE WHEN salario_base >= 3000 THEN 'Salario Alto' ELSE 'Salario Medio/Inicial' END,
    COUNT(*),
    AVG(salario_base)
FROM vendedores
GROUP BY CASE WHEN salario_base >= 3000 THEN 'Salario Alto' ELSE 'Salario Medio/Inicial' END;`,
    difficulty: 'advanced',
    why: {
      title: '¿Por qué usar UNION ALL aquí?',
      content: `**UNION ALL** combina resultados de múltiples consultas SIN eliminar duplicados. Es más rápido que UNION porque no necesita verificar duplicados.

En este ejercicio, usamos **UNION ALL** para crear un reporte consolidado que combina análisis de rangos de precios de productos y rangos de salarios de vendedores. Como son análisis diferentes (productos vs vendedores), no hay riesgo de duplicados, así que UNION ALL es más eficiente.

### ¿Por qué UNION ALL en lugar de UNION?
- **Performance**: UNION ALL es más rápido porque no verifica duplicados
- **Sin duplicados esperados**: En este caso, productos y vendedores son entidades diferentes
- **Mantiene todos los datos**: No elimina ninguna fila
- **Eficiencia**: Menos procesamiento cuando sabes que no hay duplicados

### En el mundo real:
Cuando un analista necesita crear un reporte consolidado que combine métricas de diferentes áreas (productos y empleados) en un formato similar, usa UNION ALL para combinar los resultados sin perder performance verificando duplicados innecesarios.`,
      keyPoints: [
        'UNION ALL combina resultados sin eliminar duplicados',
        'Más rápido que UNION cuando no hay riesgo de duplicados',
        'Útil para reportes consolidados de diferentes fuentes',
        'Mantiene todas las filas de todas las consultas'
      ],
      realWorldExample: 'Cuando un gerente necesita un dashboard consolidado que muestre métricas de diferentes áreas (ventas, marketing, operaciones) en el mismo formato, usa UNION ALL para combinar los resultados eficientemente.'
    },
    theory: `# UNION ALL: Combinando Sin Eliminar Duplicados

## ¿Qué es UNION ALL?

**UNION ALL** combina los resultados de múltiples consultas SELECT SIN eliminar duplicados. Es más rápido que UNION porque no necesita verificar duplicados.

### Sintaxis:
\`\`\`sql
SELECT columna1, columna2 FROM tabla1
UNION ALL
SELECT columna1, columna2 FROM tabla2;
\`\`\`

## Diferencia: UNION vs UNION ALL

### UNION
Elimina duplicados (más lento):
\`\`\`sql
SELECT nombre FROM tabla1
UNION
SELECT nombre FROM tabla2;
-- Elimina nombres duplicados, más lento
\`\`\`

### UNION ALL
Mantiene todos los registros (más rápido):
\`\`\`sql
SELECT nombre FROM tabla1
UNION ALL
SELECT nombre FROM tabla2;
-- Mantiene todos, más rápido
\`\`\`

## Cuándo Usar UNION ALL

- Cuando **no hay riesgo** de duplicados
- Cuando quieres **mejor performance**
- Cuando necesitas **mantener todas las filas**
- Cuando combinas **fuentes diferentes** (productos vs vendedores)

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    CASE 
        WHEN precio_unitario >= 500 THEN 'Producto Premium'
        WHEN precio_unitario >= 100 THEN 'Producto Medio'
        ELSE 'Producto Económico'
    END AS categoria,
    COUNT(*) AS cantidad,
    AVG(precio_unitario) AS valor_promedio
FROM productos
GROUP BY CASE 
    WHEN precio_unitario >= 500 THEN 'Producto Premium'
    WHEN precio_unitario >= 100 THEN 'Producto Medio'
    ELSE 'Producto Económico'
END
UNION ALL
SELECT 
    CASE WHEN salario_base >= 3000 THEN 'Salario Alto' ELSE 'Salario Medio/Inicial' END,
    COUNT(*),
    AVG(salario_base)
FROM vendedores
GROUP BY CASE WHEN salario_base >= 3000 THEN 'Salario Alto' ELSE 'Salario Medio/Inicial' END;
\`\`\`

Este query:
1. **Primera consulta**: Analiza rangos de precios de productos
2. **Segunda consulta**: Analiza rangos de salarios de vendedores
3. **UNION ALL**: Combina ambos análisis en un reporte consolidado
4. **Resultado**: Un reporte unificado con categorías de productos y salarios

## Ventajas de UNION ALL

- **Performance**: Más rápido que UNION
- **Simplicidad**: No verifica duplicados
- **Completitud**: Mantiene todas las filas
- **Eficiencia**: Ideal cuando no hay duplicados esperados`,
    tips: [
      'Usa UNION ALL cuando no haya riesgo de duplicados para mejor performance',
      'UNION ALL es más rápido que UNION porque no verifica duplicados',
      'Ideal para combinar fuentes diferentes (productos vs empleados)',
      'Mantiene todas las filas de todas las consultas'
    ],
    advice: [
      'Prefiere UNION ALL cuando sepas que no hay duplicados para mejor performance',
      'Usa UNION ALL para reportes consolidados de diferentes fuentes',
      'Considera UNION solo cuando necesites eliminar duplicados explícitamente',
      'Practica identificando cuándo usar UNION vs UNION ALL según el caso'
    ]
  },
  {
    id: 22,
    level: 'AVANZADO',
    title: 'Ejercicio 22: Reporte de Actividad Reciente',
    description: 'Combinar eventos recientes: ventas del último mes y nuevos vendedores de los últimos 3 meses, tomando como referencia la última fecha de actividad registrada.',
    expectedColumns: ['fecha', 'tipo_evento', 'descripcion'],
    solution: `SELECT 
    fecha_venta AS fecha,
    'Venta Registrada' AS tipo_evento,
    CONCAT('Venta #', id_venta, ' - Cantidad: ', cantidad) AS descripcion
FROM ventas
WHERE fecha_venta >= DATE_SUB((SELECT MAX(fecha_venta) FROM ventas), INTERVAL 1 MONTH)
UNION ALL
SELECT 
    fecha_ingreso,
    'Nuevo Vendedor',
    CONCAT(nombre, ' ', apellido, ' - ', departamento)
FROM vendedores
WHERE fecha_ingreso >= DATE_SUB((SELECT MAX(fecha_venta) FROM ventas), INTERVAL 3 MONTH)
ORDER BY fecha DESC;`,
    difficulty: 'advanced',
    why: {
      title: '¿Por qué usar UNION ALL con filtros temporales aquí?',
      content: `Este ejercicio necesita combinar **eventos diferentes** (ventas y nuevos vendedores) que ocurrieron en diferentes períodos de tiempo. UNION ALL permite crear una línea de tiempo unificada de actividad reciente.

Usamos **UNION ALL** porque queremos mantener todos los eventos, incluso si hay coincidencias de fecha. Cada evento tiene un tipo diferente ('Venta Registrada' vs 'Nuevo Vendedor'), así que no hay riesgo de duplicados reales.

### ¿Por qué usar MAX() como referencia en vez de CURDATE()?
Cuando los datos son históricos (no se actualizan en tiempo real), \`CURDATE()\` no sirve porque compara contra la fecha de hoy, que puede estar años después de los datos. Usar \`(SELECT MAX(fecha_venta) FROM ventas)\` como fecha de referencia hace que la query sea **independiente del momento de ejecución**.

### ¿Por qué UNION ALL con filtros temporales?
- **Línea de tiempo unificada**: Combina diferentes tipos de eventos en una sola lista
- **Períodos diferentes**: Cada tipo de evento puede tener su propio período relevante
- **Análisis de actividad**: Permite ver toda la actividad reciente en un solo lugar
- **Flexibilidad**: Puedes filtrar cada tipo de evento independientemente

### En el mundo real:
Cuando un gerente necesita un dashboard de actividad reciente que muestre diferentes tipos de eventos (ventas, contrataciones, cambios de estado) en una línea de tiempo unificada, usa UNION ALL para combinar eventos de diferentes tablas con sus propios filtros temporales. En producción con datos en vivo, usarías \`CURDATE()\` directamente.`,
      keyPoints: [
        'UNION ALL permite combinar eventos de diferentes tablas',
        'Cada consulta puede tener sus propios filtros temporales',
        'Usar MAX() como referencia hace la query robusta con datos históricos',
        'CONCAT ayuda a crear descripciones legibles para cada evento'
      ],
      realWorldExample: 'Cuando un sistema necesita mostrar un feed de actividad reciente combinando eventos de diferentes módulos (ventas, soporte, marketing), usa UNION ALL para crear una línea de tiempo unificada.'
    },
    theory: `# UNION ALL con Filtros Temporales: Líneas de Tiempo Unificadas

## Combinando Eventos Temporales

UNION ALL es ideal para combinar eventos de diferentes tablas que ocurren en el tiempo, creando una línea de tiempo unificada.

## Fecha de Referencia Dinámica

Cuando trabajás con datos históricos, en vez de usar \\\`CURDATE()\\\` (que te da la fecha de hoy), podés usar una **subquery** para obtener la fecha de referencia desde los propios datos:

\`\`\`sql
-- En producción (datos en tiempo real):
WHERE fecha >= DATE_SUB(CURDATE(), INTERVAL 1 MONTH)

-- Con datos históricos (más robusto):
WHERE fecha >= DATE_SUB((SELECT MAX(fecha) FROM tabla), INTERVAL 1 MONTH)
\`\`\`

Esto hace que tu query funcione sin importar cuándo se ejecute.

## Filtros Temporales Independientes

Cada consulta en UNION ALL puede tener sus propios filtros:

\`\`\`sql
SELECT fecha, 'Tipo1' AS evento FROM tabla1
WHERE fecha >= DATE_SUB((SELECT MAX(fecha) FROM tabla1), INTERVAL 1 MONTH)
UNION ALL
SELECT fecha, 'Tipo2' AS evento FROM tabla2
WHERE fecha >= DATE_SUB((SELECT MAX(fecha) FROM tabla1), INTERVAL 3 MONTH);
\`\`\`

Cada tipo de evento puede tener su propio período relevante.

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    fecha_venta AS fecha,
    'Venta Registrada' AS tipo_evento,
    CONCAT('Venta #', id_venta, ' - Cantidad: ', cantidad) AS descripcion
FROM ventas
WHERE fecha_venta >= DATE_SUB((SELECT MAX(fecha_venta) FROM ventas), INTERVAL 1 MONTH)
UNION ALL
SELECT 
    fecha_ingreso,
    'Nuevo Vendedor',
    CONCAT(nombre, ' ', apellido, ' - ', departamento)
FROM vendedores
WHERE fecha_ingreso >= DATE_SUB((SELECT MAX(fecha_venta) FROM ventas), INTERVAL 3 MONTH)
ORDER BY fecha DESC;
\`\`\`

Este query:
1. **Subquery de referencia**: Obtiene la última fecha de venta como punto de referencia
2. **Primera consulta**: Ventas del último mes relativo a esa fecha
3. **Segunda consulta**: Vendedores nuevos de últimos 3 meses relativos a la misma fecha
4. **UNION ALL**: Combina ambos en una línea de tiempo
5. **ORDER BY**: Ordena por fecha descendente (más reciente primero)

## CONCAT para Descripciones

CONCAT ayuda a crear descripciones legibles:
\`\`\`sql
CONCAT('Venta #', id_venta, ' - Cantidad: ', cantidad)
-- Resultado: "Venta #123 - Cantidad: 5"
\`\`\`

## Casos de Uso Comunes

### Feeds de Actividad
\`\`\`sql
SELECT fecha, 'Venta' AS tipo, descripcion FROM ventas
UNION ALL
SELECT fecha, 'Devolución' AS tipo, descripcion FROM devoluciones
ORDER BY fecha DESC;
\`\`\`

## Ventajas

- **Línea de Tiempo Unificada**: Combina diferentes tipos de eventos
- **Filtros Independientes**: Cada tipo puede tener su propio período
- **Análisis Completo**: Ver toda la actividad en un solo lugar
- **Flexibilidad**: Fácil agregar más tipos de eventos
- **Robustez**: Usar MAX() como referencia hace la query independiente de cuándo se ejecute`,
    tips: [
      'Usá (SELECT MAX(fecha_venta) FROM ventas) como fecha de referencia en vez de CURDATE()',
      'Cada consulta en UNION ALL puede tener sus propios filtros WHERE',
      'Usa CONCAT para crear descripciones legibles de eventos',
      'ORDER BY se aplica al resultado final combinado',
      'UNION ALL mantiene todos los eventos, incluso con fechas similares'
    ],
    advice: [
      'Usa UNION ALL para crear líneas de tiempo unificadas de diferentes tipos de eventos',
      'Cuando los datos son históricos, usá MAX() como referencia temporal en vez de CURDATE()',
      'Usa CONCAT para crear descripciones informativas de cada evento',
      'Practica combinando diferentes tipos de eventos temporales en reportes unificados'
    ]
  },
  // NIVEL AVANZADO
  {
    id: 23,
    level: 'AVANZADO',
    title: 'Ejercicio 23: Listado de Ventas con Información Completa',
    description: 'Mostrar todas las ventas con datos del vendedor y producto.',
    expectedColumns: ['fecha_venta', 'nombre_vendedor', 'nombre_producto', 'cantidad', 'precio_total'],
    solution: `SELECT 
    v.fecha_venta,
    CONCAT(vend.nombre, ' ', vend.apellido) AS nombre_vendedor,
    p.nombre_producto,
    v.cantidad,
    (v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) AS precio_total
FROM ventas v
INNER JOIN vendedores vend ON v.id_vendedor = vend.id_vendedor
INNER JOIN productos p ON v.id_producto = p.id_producto
ORDER BY v.fecha_venta DESC;`,
    difficulty: 'advanced',
    why: {
      title: '¿Por qué este ejercicio es avanzado?',
      content: `Este ejercicio combina múltiples conceptos aprendidos anteriormente: **múltiples JOINs**, **CONCAT** para nombres completos, y **expresiones aritméticas complejas** para calcular precios totales con descuentos.

Aunque usa conceptos básicos, se considera avanzado porque requiere **integrar múltiples técnicas** en una sola consulta: combinar tres tablas, formatear texto, y realizar cálculos complejos. Esto demuestra dominio de SQL básico aplicado a casos reales.

### ¿Por qué es importante integrar conceptos?
- **Casos reales**: Los problemas del mundo real requieren múltiples técnicas
- **Eficiencia**: Una consulta bien estructurada es mejor que múltiples
- **Mantenibilidad**: Código que integra conceptos es más fácil de mantener
- **Habilidad**: Demuestra comprensión profunda de cómo funcionan las técnicas juntas

### En el mundo real:
Cuando un analista necesita un reporte completo de ventas con información detallada del vendedor y producto, integra múltiples JOINs, CONCAT y expresiones en una sola consulta eficiente.`,
      keyPoints: [
        'Los ejercicios avanzados integran múltiples conceptos básicos',
        'Múltiples JOINs permiten obtener información completa de varias tablas',
        'CONCAT formatea datos para hacerlos más legibles',
        'Las expresiones complejas calculan valores derivados en la misma consulta'
      ],
      realWorldExample: 'Cuando un gerente necesita un reporte ejecutivo con información completa de transacciones, integra JOINs, formateo de texto y cálculos en una sola consulta bien estructurada.'
    },
    theory: `# Integrando Múltiples Conceptos: Consultas Avanzadas

## Consultas que Integran Técnicas

Los ejercicios avanzados combinan múltiples conceptos básicos en consultas más complejas y realistas.

## Conceptos Integrados en este Ejercicio

### 1. Múltiples JOINs
Combina tres tablas relacionadas:
\`\`\`sql
FROM ventas v
INNER JOIN vendedores vend ON v.id_vendedor = vend.id_vendedor
INNER JOIN productos p ON v.id_producto = p.id_producto
\`\`\`

### 2. CONCAT para Formateo
Crea nombres completos legibles:
\`\`\`sql
CONCAT(vend.nombre, ' ', vend.apellido) AS nombre_vendedor
\`\`\`

### 3. Expresiones Aritméticas Complejas
Calcula precios con descuentos:
\`\`\`sql
(v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) AS precio_total
\`\`\`
Esto calcula: cantidad × precio × (1 - descuento%)

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    v.fecha_venta,
    CONCAT(vend.nombre, ' ', vend.apellido) AS nombre_vendedor,
    p.nombre_producto,
    v.cantidad,
    (v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) AS precio_total
FROM ventas v
INNER JOIN vendedores vend ON v.id_vendedor = vend.id_vendedor
INNER JOIN productos p ON v.id_producto = p.id_producto
ORDER BY v.fecha_venta DESC;
\`\`\`

Este query integra:
1. **Múltiples JOINs** para combinar tablas
2. **CONCAT** para formatear nombres
3. **Expresiones complejas** para cálculos
4. **ORDER BY** para ordenar resultados

## Ventajas de Integrar Conceptos

- **Eficiencia**: Una consulta obtiene todo lo necesario
- **Claridad**: Código estructurado y legible
- **Mantenibilidad**: Fácil de modificar y extender
- **Realismo**: Refleja problemas del mundo real`,
    tips: [
      'Combina múltiples técnicas para resolver problemas complejos',
      'Usa alias descriptivos cuando integras múltiples JOINs',
      'CONCAT ayuda a crear campos más legibles en reportes',
      'Las expresiones complejas calculan valores derivados eficientemente'
    ],
    advice: [
      'Practica integrando múltiples conceptos en consultas más complejas',
      'Estructura tus consultas de forma clara y legible',
      'Usa alias descriptivos para hacer el código más mantenible',
      'Considera cómo cada técnica contribuye al resultado final'
    ]
  },
  {
    id: 24,
    level: 'AVANZADO',
    title: 'Ejercicio 24: Ventas por Categoría',
    description: 'Calcular ventas totales agrupadas por categoría de producto.',
    expectedColumns: ['categoria', 'total_ventas', 'cantidad_productos_vendidos'],
    solution: `SELECT 
    p.categoria,
    SUM(v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) AS total_ventas,
    SUM(v.cantidad) AS cantidad_productos_vendidos
FROM ventas v
INNER JOIN productos p ON v.id_producto = p.id_producto
GROUP BY p.categoria
ORDER BY total_ventas DESC;`,
    difficulty: 'advanced',
    why: {
      title: '¿Por qué este ejercicio es avanzado?',
      content: `Este ejercicio combina **JOINs**, **GROUP BY**, **funciones de agregación** y **expresiones complejas** para crear un análisis agregado por categoría.

Aunque cada concepto individual es básico, la **combinación** de todos ellos en una consulta eficiente requiere entender cómo funcionan juntos: primero combinas tablas con JOIN, luego agrupas con GROUP BY, y finalmente calculas agregaciones con expresiones complejas.

### ¿Por qué es importante entender cómo se combinan?
- **Análisis real**: Los análisis de negocio requieren múltiples técnicas
- **Eficiencia**: Una consulta bien estructurada procesa datos eficientemente
- **Claridad**: Código que combina técnicas de forma lógica es más fácil de entender
- **Escalabilidad**: Consultas bien estructuradas funcionan bien con grandes volúmenes

### En el mundo real:
Cuando un gerente necesita un análisis de ventas por categoría con totales y cantidades, combina JOINs para obtener datos relacionados, GROUP BY para agrupar por categoría, y funciones de agregación con expresiones para calcular métricas complejas.`,
      keyPoints: [
        'GROUP BY con JOINs permite análisis agregados de datos relacionados',
        'SUM con expresiones calcula totales complejos (con descuentos)',
        'Las funciones de agregación procesan grupos eficientemente',
        'ORDER BY ordena los grupos por sus métricas calculadas'
      ],
      realWorldExample: 'Cuando un analista necesita un reporte de ventas por región con totales y promedios, combina JOINs, GROUP BY y funciones de agregación en una sola consulta estructurada.'
    },
    theory: `# GROUP BY con JOINs: Análisis Agregado de Datos Relacionados

## Combinando GROUP BY y JOINs

Cuando necesitas analizar datos agregados de tablas relacionadas, combinas JOINs con GROUP BY:

\`\`\`sql
SELECT categoria, SUM(monto) AS total
FROM ventas v
INNER JOIN productos p ON v.id_producto = p.id_producto
GROUP BY categoria;
\`\`\`

## Expresiones Complejas en Agregaciones

Puedes usar expresiones complejas dentro de funciones de agregación:

\`\`\`sql
SUM(v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) AS total_ventas
\`\`\`

Esto calcula el total considerando descuentos.

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    p.categoria,
    SUM(v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) AS total_ventas,
    SUM(v.cantidad) AS cantidad_productos_vendidos
FROM ventas v
INNER JOIN productos p ON v.id_producto = p.id_producto
GROUP BY p.categoria
ORDER BY total_ventas DESC;
\`\`\`

Este query:
1. **Combina** ventas con productos usando JOIN
2. **Agrupa** por categoría de producto
3. **Calcula** total de ventas con expresión compleja (considerando descuentos)
4. **Suma** cantidad total de productos vendidos
5. **Ordena** por total de ventas descendente

## Orden de Ejecución

SQL ejecuta en este orden:
1. **FROM + JOIN**: Combina las tablas
2. **WHERE**: Filtra filas (si existe)
3. **GROUP BY**: Agrupa por categoría
4. **SELECT**: Calcula agregaciones
5. **ORDER BY**: Ordena los grupos

## Ventajas

- **Análisis Eficiente**: Una consulta obtiene análisis completo
- **Métricas Complejas**: Puedes calcular totales con descuentos, márgenes, etc.
- **Agrupación Inteligente**: Agrupa datos relacionados de múltiples tablas
- **Resultados Accionables**: Proporciona insights por categoría`,
    tips: [
      'GROUP BY se aplica después de los JOINs',
      'Puedes usar expresiones complejas dentro de funciones de agregación',
      'Todas las columnas no agregadas deben estar en GROUP BY',
      'ORDER BY puede usar alias de funciones de agregación'
    ],
    advice: [
      'Combina JOINs con GROUP BY para análisis agregados de datos relacionados',
      'Usa expresiones complejas en agregaciones para calcular métricas realistas',
      'Practica creando análisis por diferentes dimensiones (categoría, región, tiempo)',
      'Considera el orden de ejecución cuando estructuras consultas complejas'
    ]
  },
  {
    id: 25,
    level: 'AVANZADO',
    title: 'Ejercicio 25: Vendedores sin Ventas',
    description: 'Identificar qué vendedores no han realizado ninguna venta.',
    expectedColumns: ['id_vendedor', 'nombre_vendedor', 'departamento', 'fecha_ingreso'],
    solution: `SELECT 
    vend.id_vendedor,
    CONCAT(vend.nombre, ' ', vend.apellido) AS nombre_vendedor,
    vend.departamento,
    vend.fecha_ingreso
FROM vendedores vend
LEFT JOIN ventas v ON vend.id_vendedor = v.id_vendedor
WHERE v.id_venta IS NULL
ORDER BY vend.fecha_ingreso;`,
    difficulty: 'advanced',
    why: {
      title: '¿Por qué este ejercicio es avanzado?',
      content: `Este ejercicio combina **LEFT JOIN** con **WHERE IS NULL** y **CONCAT** para identificar vendedores sin actividad. Aunque cada concepto es básico, la combinación requiere entender cómo LEFT JOIN crea NULLs y cómo filtrarlos.

Se considera avanzado porque requiere **pensamiento analítico**: identificar qué vendedores NO tienen relaciones en la tabla de ventas. Esto es útil para análisis de desempeño, identificación de problemas, y toma de decisiones estratégicas.

### ¿Por qué identificar registros sin relaciones?
- **Análisis de desempeño**: Identifica vendedores que necesitan apoyo
- **Calidad de datos**: Encuentra inconsistencias o datos faltantes
- **Toma de decisiones**: Informa decisiones sobre capacitación o reasignación
- **Monitoreo**: Permite identificar problemas tempranamente

### En el mundo real:
Cuando un gerente necesita identificar vendedores que no han realizado ventas para programas de capacitación o evaluación de desempeño, usa LEFT JOIN con IS NULL para encontrar estos casos específicos.`,
      keyPoints: [
        'LEFT JOIN crea NULLs cuando no hay coincidencias',
        'WHERE columna IS NULL filtra filas sin relaciones',
        'CONCAT formatea nombres para hacerlos más legibles',
        'Esta técnica es útil para análisis de completitud y desempeño'
      ],
      realWorldExample: 'Cuando RRHH necesita identificar empleados que no han completado capacitaciones obligatorias, usa LEFT JOIN empleados con capacitaciones WHERE id_capacitacion IS NULL.'
    },
    theory: `# LEFT JOIN con IS NULL: Encontrar Registros Sin Relaciones

## Identificando Valores Faltantes

LEFT JOIN + WHERE IS NULL es el patrón estándar para encontrar registros sin relaciones:

\`\`\`sql
SELECT tabla_izquierda.*
FROM tabla_izquierda
LEFT JOIN tabla_derecha ON condicion
WHERE tabla_derecha.id IS NULL;
\`\`\`

## Cómo Funciona

1. **LEFT JOIN**: Incluye todas las filas de la tabla izquierda
2. **NULLs**: Cuando no hay coincidencia, columnas de tabla derecha son NULL
3. **WHERE IS NULL**: Filtra solo las filas sin coincidencias

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    vend.id_vendedor,
    CONCAT(vend.nombre, ' ', vend.apellido) AS nombre_vendedor,
    vend.departamento,
    vend.fecha_ingreso
FROM vendedores vend
LEFT JOIN ventas v ON vend.id_vendedor = v.id_vendedor
WHERE v.id_venta IS NULL
ORDER BY vend.fecha_ingreso;
\`\`\`

Este query:
1. **Incluye** todos los vendedores (LEFT JOIN)
2. **Combina** con ventas (puede ser NULL)
3. **Filtra** solo vendedores sin ventas (WHERE IS NULL)
4. **Formatea** nombres con CONCAT
5. **Ordena** por fecha de ingreso

## Casos de Uso Comunes

### Productos Sin Ventas
\`\`\`sql
SELECT p.* FROM productos p
LEFT JOIN ventas v ON p.id = v.id_producto
WHERE v.id_producto IS NULL;
\`\`\`

### Clientes Sin Pedidos
\`\`\`sql
SELECT c.* FROM clientes c
LEFT JOIN pedidos p ON c.id = p.id_cliente
WHERE p.id_cliente IS NULL;
\`\`\`

## Ventajas

- **Análisis de Completitud**: Encuentra datos faltantes
- **Identificación de Problemas**: Detecta registros sin actividad
- **Toma de Decisiones**: Informa acciones correctivas
- **Monitoreo**: Permite seguimiento proactivo`,
    tips: [
      'LEFT JOIN + WHERE IS NULL es el patrón estándar para encontrar valores faltantes',
      'Usa CONCAT para formatear nombres y hacer resultados más legibles',
      'Filtra por la clave primaria de la tabla derecha (id_venta) para mayor precisión',
      'Esta técnica es esencial para análisis de completitud y calidad de datos'
    ],
    advice: [
      'Usa LEFT JOIN + IS NULL para identificar registros sin relaciones',
      'Practica identificando diferentes tipos de valores faltantes en tus datos',
      'Considera esta técnica para análisis de desempeño y calidad de datos',
      'Combina con CONCAT para hacer los resultados más legibles y profesionales'
    ]
  },
  {
    id: 26,
    level: 'AVANZADO',
    title: 'Ejercicio 26: Categorización de Descuentos',
    description: 'Analizar y clasificar descuentos: \'Sin Descuento\' (0%), \'Descuento Bajo\' (1-7%), \'Descuento Medio\' (8-12%), \'Descuento Alto\' (>12%).',
    expectedColumns: ['id_venta', 'fecha_venta', 'descuento_aplicado', 'tipo_descuento'],
    solution: `SELECT 
    id_venta,
    fecha_venta,
    descuento_aplicado,
    CASE 
        WHEN descuento_aplicado = 0 THEN 'Sin Descuento'
        WHEN descuento_aplicado BETWEEN 1 AND 7 THEN 'Descuento Bajo'
        WHEN descuento_aplicado BETWEEN 8 AND 12 THEN 'Descuento Medio'
        ELSE 'Descuento Alto'
    END AS tipo_descuento
FROM ventas
ORDER BY fecha_venta DESC;`,
    difficulty: 'advanced',
    why: {
      title: '¿Por qué usar CASE con BETWEEN aquí?',
      content: `Este ejercicio usa **CASE WHEN** con **BETWEEN** para clasificar descuentos en rangos. Aunque CASE es básico, combinarlo con BETWEEN para crear rangos es una técnica avanzada que permite segmentación precisa.

**BETWEEN** es útil para definir rangos inclusivos (incluye ambos extremos). Combinado con CASE, permite crear categorías basadas en rangos numéricos de forma clara y mantenible.

### ¿Por qué BETWEEN en lugar de múltiples condiciones?
- **Claridad**: BETWEEN hace explícito que es un rango
- **Legibilidad**: Más fácil de leer que múltiples condiciones con AND
- **Mantenibilidad**: Más fácil modificar rangos
- **Precisión**: BETWEEN es inclusivo en ambos extremos (1 AND 7 incluye ambos)

### En el mundo real:
Cuando un analista necesita clasificar transacciones por monto en rangos (Bajo: $0-$100, Medio: $101-$500, Alto: >$500) para análisis segmentado, usa CASE con BETWEEN para crear estas categorías de forma clara.`,
      keyPoints: [
        'BETWEEN es inclusivo en ambos extremos (incluye los valores límite)',
        'CASE con BETWEEN permite crear categorías basadas en rangos',
        'BETWEEN es más legible que múltiples condiciones con AND',
        'Útil para segmentación y clasificación por rangos numéricos'
      ],
      realWorldExample: 'Cuando un gerente necesita clasificar clientes por nivel de gasto en rangos específicos para estrategias de marketing diferenciadas, usa CASE con BETWEEN para crear estas categorías.'
    },
    theory: `# CASE con BETWEEN: Clasificación por Rangos

## BETWEEN: Operador de Rango

**BETWEEN** verifica si un valor está dentro de un rango (inclusivo en ambos extremos):

\`\`\`sql
WHERE valor BETWEEN inicio AND fin
\`\`\`
Equivale a: \`valor >= inicio AND valor <= fin\`

## CASE con BETWEEN

Combina CASE con BETWEEN para crear categorías basadas en rangos:

\`\`\`sql
CASE 
    WHEN valor BETWEEN 1 AND 7 THEN 'Bajo'
    WHEN valor BETWEEN 8 AND 12 THEN 'Medio'
    ELSE 'Alto'
END
\`\`\`

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    id_venta,
    fecha_venta,
    descuento_aplicado,
    CASE 
        WHEN descuento_aplicado = 0 THEN 'Sin Descuento'
        WHEN descuento_aplicado BETWEEN 1 AND 7 THEN 'Descuento Bajo'
        WHEN descuento_aplicado BETWEEN 8 AND 12 THEN 'Descuento Medio'
        ELSE 'Descuento Alto'
    END AS tipo_descuento
FROM ventas
ORDER BY fecha_venta DESC;
\`\`\`

Este query:
1. **Clasifica** descuentos usando CASE
2. **Usa BETWEEN** para definir rangos (1-7, 8-12)
3. **Crea** categorías descriptivas
4. **Ordena** por fecha descendente

## BETWEEN: Inclusivo

BETWEEN es **inclusivo** en ambos extremos:
- \`BETWEEN 1 AND 7\` incluye 1, 2, 3, 4, 5, 6, 7
- Equivale a: \`>= 1 AND <= 7\`

## Casos de Uso Comunes

### Clasificación por Edad
\`\`\`sql
CASE 
    WHEN edad BETWEEN 18 AND 25 THEN 'Joven'
    WHEN edad BETWEEN 26 AND 40 THEN 'Adulto'
    ELSE 'Senior'
END
\`\`\`

### Segmentación por Monto
\`\`\`sql
CASE 
    WHEN monto BETWEEN 0 AND 100 THEN 'Bajo'
    WHEN monto BETWEEN 101 AND 500 THEN 'Medio'
    ELSE 'Alto'
END
\`\`\`

## Ventajas de BETWEEN

- **Claridad**: Hace explícito que es un rango
- **Legibilidad**: Más fácil de leer que múltiples AND
- **Mantenibilidad**: Fácil modificar rangos
- **Precisión**: Inclusivo en ambos extremos`,
    tips: [
      'BETWEEN es inclusivo en ambos extremos (incluye los valores límite)',
      'CASE evalúa condiciones en orden, pon rangos más específicos primero',
      'BETWEEN es más legible que múltiples condiciones con AND',
      'Útil para crear categorías basadas en rangos numéricos'
    ],
    advice: [
      'Usa BETWEEN cuando necesites verificar rangos de valores',
      'Combina CASE con BETWEEN para crear categorías basadas en rangos',
      'Practica creando diferentes segmentaciones usando rangos',
      'Considera BETWEEN para hacer código más legible y mantenible'
    ]
  },
  {
    id: 27,
    level: 'AVANZADO',
    title: 'Ejercicio 27: Top 3 Productos Más Vendidos',
    description: 'Identificar los 3 productos con mayor cantidad vendida.',
    expectedColumns: ['ranking', 'nombre_producto', 'categoria', 'cantidad_total_vendida', 'ingresos_generados'],
    solution: `SELECT 
    ROW_NUMBER() OVER (ORDER BY SUM(v.cantidad) DESC) AS ranking,
    p.nombre_producto,
    p.categoria,
    SUM(v.cantidad) AS cantidad_total_vendida,
    SUM(v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) AS ingresos_generados
FROM ventas v
INNER JOIN productos p ON v.id_producto = p.id_producto
GROUP BY p.id_producto, p.nombre_producto, p.categoria
ORDER BY cantidad_total_vendida DESC
LIMIT 3;`,
    difficulty: 'advanced',
    why: {
      title: '¿Por qué usar ROW_NUMBER() con GROUP BY aquí?',
      content: `**ROW_NUMBER()** es una función de ventana (window function) que asigna un número secuencial a cada fila según un orden especificado. En este ejercicio, se usa con **GROUP BY** para crear rankings después de agrupar datos.

Las **window functions** como ROW_NUMBER() operan sobre un conjunto de filas relacionadas sin agruparlas en una sola fila como las funciones de agregación tradicionales. Esto permite crear rankings, números de fila, y cálculos basados en ventanas de datos.

### ¿Por qué ROW_NUMBER() en lugar de solo LIMIT?
- **Ranking explícito**: Muestra el número de posición (1, 2, 3)
- **Flexibilidad**: Puedes filtrar por ranking específico (ej: solo el segundo)
- **Análisis**: Permite ver posiciones relativas, no solo top N
- **Window functions**: Más poderosas que agregaciones tradicionales

### En el mundo real:
Cuando un gerente necesita un ranking de productos más vendidos con sus posiciones explícitas para análisis competitivo, usa ROW_NUMBER() para asignar posiciones y luego puede filtrar o mostrar según necesidad.`,
      keyPoints: [
        'ROW_NUMBER() asigna números secuenciales según un orden',
        'Window functions operan sobre ventanas de datos sin agrupar',
        'Se puede usar con GROUP BY para rankings después de agrupar',
        'OVER especifica cómo se ordenan las filas para el ranking'
      ],
      realWorldExample: 'Cuando un analista necesita crear un ranking de vendedores con sus posiciones para un programa de incentivos, usa ROW_NUMBER() para asignar posiciones basadas en ventas totales.'
    },
    theory: `# ROW_NUMBER(): Funciones de Ventana (Window Functions)

## ¿Qué son las Window Functions?

Las **window functions** (funciones de ventana) realizan cálculos sobre un conjunto de filas relacionadas sin agruparlas en una sola fila. Operan sobre una "ventana" de datos.

### Sintaxis:
\`\`\`sql
ROW_NUMBER() OVER (ORDER BY columna)
\`\`\`

## ROW_NUMBER()

**ROW_NUMBER()** asigna un número secuencial (1, 2, 3...) a cada fila según el orden especificado en OVER:

\`\`\`sql
ROW_NUMBER() OVER (ORDER BY ventas DESC) AS ranking
\`\`\`
Asigna 1 al que tiene más ventas, 2 al segundo, etc.

## Diferencia: Agregaciones vs Window Functions

### Agregaciones Tradicionales
Agrupan filas en una sola:
\`\`\`sql
SELECT categoria, SUM(ventas) FROM tabla GROUP BY categoria;
-- Devuelve una fila por categoría
\`\`\`

### Window Functions
Mantienen todas las filas y agregan información:
\`\`\`sql
SELECT producto, ventas, ROW_NUMBER() OVER (ORDER BY ventas DESC) AS ranking
FROM tabla;
-- Devuelve todas las filas con ranking agregado
\`\`\`

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    ROW_NUMBER() OVER (ORDER BY SUM(v.cantidad) DESC) AS ranking,
    p.nombre_producto,
    p.categoria,
    SUM(v.cantidad) AS cantidad_total_vendida,
    SUM(v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) AS ingresos_generados
FROM ventas v
INNER JOIN productos p ON v.id_producto = p.id_producto
GROUP BY p.id_producto, p.nombre_producto, p.categoria
ORDER BY cantidad_total_vendida DESC
LIMIT 3;
\`\`\`

Este query:
1. **Agrupa** ventas por producto
2. **Calcula** agregaciones (SUM)
3. **Asigna** ranking usando ROW_NUMBER()
4. **Ordena** y limita a top 3

## OVER: Especificando la Ventana

**OVER** define cómo se ordenan las filas para la función de ventana:
\`\`\`sql
ROW_NUMBER() OVER (ORDER BY ventas DESC)
\`\`\`
Ordena por ventas descendente antes de asignar números.

## Casos de Uso Comunes

### Rankings
\`\`\`sql
SELECT producto, ventas,
       ROW_NUMBER() OVER (ORDER BY ventas DESC) AS ranking
FROM productos;
\`\`\`

### Top N por Grupo
\`\`\`sql
SELECT categoria, producto, ventas,
       ROW_NUMBER() OVER (PARTITION BY categoria ORDER BY ventas DESC) AS ranking_categoria
FROM productos;
\`\`\`

## Ventajas de Window Functions

- **Rankings**: Crea posiciones relativas
- **Flexibilidad**: Mantiene todas las filas
- **Análisis Avanzado**: Permite cálculos complejos sobre ventanas
- **Performance**: Optimizadas por el motor SQL`,
    tips: [
      'ROW_NUMBER() asigna números secuenciales según el orden en OVER',
      'Window functions no agrupan filas como las agregaciones tradicionales',
      'OVER especifica cómo se ordenan las filas para la función',
      'Puedes combinar window functions con GROUP BY para rankings después de agrupar'
    ],
    advice: [
      'Usa ROW_NUMBER() cuando necesites crear rankings explícitos',
      'Practica usando diferentes window functions (ROW_NUMBER, RANK, DENSE_RANK)',
      'Combina window functions con GROUP BY para análisis más complejos',
      'Considera window functions para análisis que requieren mantener todas las filas'
    ]
  },
  {
    id: 28,
    level: 'AVANZADO',
    title: 'Ejercicio 28: Segundo Vendedor con Más Ventas (en monto)',
    description: 'Encontrar el vendedor en segundo lugar por monto total vendido.',
    expectedColumns: ['nombre_vendedor', 'departamento', 'total_vendido', 'ranking_ventas'],
    solution: `WITH ranking_vendedores AS (
    SELECT 
        vend.nombre,
        vend.apellido,
        vend.departamento,
        SUM(v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) AS total_vendido,
        ROW_NUMBER() OVER (ORDER BY SUM(v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) DESC) AS ranking_ventas
    FROM ventas v
    INNER JOIN vendedores vend ON v.id_vendedor = vend.id_vendedor
    INNER JOIN productos p ON v.id_producto = p.id_producto
    GROUP BY vend.id_vendedor, vend.nombre, vend.apellido, vend.departamento
)
SELECT 
    CONCAT(nombre, ' ', apellido) AS nombre_vendedor,
    departamento,
    total_vendido,
    ranking_ventas
FROM ranking_vendedores
WHERE ranking_ventas = 2;`,
    difficulty: 'advanced',
    why: {
      title: '¿Por qué usar CTE con ROW_NUMBER() aquí?',
      content: `Este ejercicio necesita encontrar el **segundo lugar** en un ranking. Para hacer esto, primero calculas el ranking usando **ROW_NUMBER()** dentro de un CTE, y luego filtras por ranking = 2.

Usar **CTE con ROW_NUMBER()** es más eficiente y legible que usar subconsultas. El CTE calcula el ranking una vez, y luego puedes filtrar fácilmente por cualquier posición (1, 2, 3, etc.).

### ¿Por qué CTE + ROW_NUMBER() en lugar de subconsulta?
- **Legibilidad**: El CTE hace el código más claro
- **Reutilización**: Puedes usar el ranking calculado múltiples veces
- **Flexibilidad**: Fácil cambiar el filtro (WHERE ranking = 2, 3, etc.)
- **Performance**: El ranking se calcula una vez y se reutiliza

### En el mundo real:
Cuando un gerente necesita identificar al segundo mejor vendedor para reconocimiento o análisis comparativo, usa CTE con ROW_NUMBER() para calcular rankings y luego filtra por la posición específica que necesita.`,
      keyPoints: [
        'CTE con ROW_NUMBER() permite calcular rankings y luego filtrarlos',
        'ROW_NUMBER() asigna posiciones secuenciales (1, 2, 3...)',
        'Puedes filtrar por cualquier posición usando WHERE ranking = N',
        'CTE hace el código más legible que subconsultas anidadas'
      ],
      realWorldExample: 'Cuando un analista necesita encontrar el segundo mejor desempeño en cualquier métrica (ventas, satisfacción, eficiencia), usa CTE con ROW_NUMBER() para calcular rankings y filtrar por posición.'
    },
    theory: `# CTE con ROW_NUMBER(): Rankings y Filtrado por Posición

## Calculando Rankings con CTE

Puedes usar CTE para calcular rankings y luego filtrar por posición específica:

\`\`\`sql
WITH rankings AS (
    SELECT nombre, ventas,
           ROW_NUMBER() OVER (ORDER BY ventas DESC) AS ranking
    FROM vendedores
)
SELECT * FROM rankings WHERE ranking = 2;
\`\`\`

## Ventajas del Patrón CTE + ROW_NUMBER()

### 1. Legibilidad
El CTE separa el cálculo del ranking del filtrado:
\`\`\`sql
WITH ranking_vendedores AS (
    -- Calcula ranking
    SELECT ..., ROW_NUMBER() OVER (...) AS ranking
    FROM ...
)
SELECT * FROM ranking_vendedores WHERE ranking = 2;
-- Filtra por posición
\`\`\`

### 2. Flexibilidad
Puedes cambiar fácilmente la posición:
- \`WHERE ranking = 2\` - Segundo lugar
- \`WHERE ranking <= 3\` - Top 3
- \`WHERE ranking BETWEEN 2 AND 5\` - Posiciones 2-5

## Ejemplo Completo del Ejercicio

\`\`\`sql
WITH ranking_vendedores AS (
    SELECT 
        vend.nombre, vend.apellido, vend.departamento,
        SUM(...) AS total_vendido,
        ROW_NUMBER() OVER (ORDER BY SUM(...) DESC) AS ranking_ventas
    FROM ventas v
    INNER JOIN vendedores vend ON v.id_vendedor = vend.id_vendedor
    INNER JOIN productos p ON v.id_producto = p.id_producto
    GROUP BY vend.id_vendedor, vend.nombre, vend.apellido, vend.departamento
)
SELECT 
    CONCAT(nombre, ' ', apellido) AS nombre_vendedor,
    departamento, total_vendido, ranking_ventas
FROM ranking_vendedores
WHERE ranking_ventas = 2;
\`\`\`

Este query:
1. **Calcula** ventas totales por vendedor
2. **Asigna** ranking usando ROW_NUMBER()
3. **Filtra** solo el segundo lugar (ranking = 2)
4. **Formatea** nombre con CONCAT

## Casos de Uso Comunes

### Top N Flexible
\`\`\`sql
WITH rankings AS (
    SELECT *, ROW_NUMBER() OVER (ORDER BY ventas DESC) AS ranking
    FROM productos
)
SELECT * FROM rankings WHERE ranking <= 5;
\`\`\`

### Posiciones Específicas
\`\`\`sql
WITH rankings AS (...)
SELECT * FROM rankings WHERE ranking IN (2, 5, 10);
\`\`\`

## Ventajas

- **Flexibilidad**: Fácil cambiar la posición filtrada
- **Legibilidad**: Código claro y estructurado
- **Reutilización**: Puedes usar el CTE para múltiples filtros
- **Mantenibilidad**: Fácil modificar lógica de ranking`,
    tips: [
      'CTE con ROW_NUMBER() permite calcular rankings y filtrarlos después',
      'Puedes filtrar por cualquier posición: WHERE ranking = N',
      'ROW_NUMBER() asigna números secuenciales sin gaps',
      'Combina con GROUP BY para rankings después de agrupar'
    ],
    advice: [
      'Usa CTE con ROW_NUMBER() cuando necesites filtrar por posición específica',
      'Practica creando rankings y filtrando por diferentes posiciones',
      'Considera este patrón para análisis que requieren posiciones específicas',
      'Combina con GROUP BY para rankings de grupos agregados'
    ]
  },
  {
    id: 29,
    level: 'AVANZADO',
    title: 'Ejercicio 29: Rentabilidad por Vendedor',
    description: 'Calcular ganancia neta y margen para cada vendedor.',
    expectedColumns: ['nombre_vendedor', 'departamento', 'total_ingresos', 'total_costos', 'ganancia_neta', 'margen_porcentual'],
    solution: `SELECT 
    CONCAT(vend.nombre, ' ', vend.apellido) AS nombre_vendedor,
    vend.departamento,
    SUM(v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) AS total_ingresos,
    SUM(v.cantidad * p.costo_unitario) AS total_costos,
    SUM(v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) - SUM(v.cantidad * p.costo_unitario) AS ganancia_neta,
    ((SUM(v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) - SUM(v.cantidad * p.costo_unitario)) / 
     SUM(v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) * 100) AS margen_porcentual
FROM ventas v
INNER JOIN vendedores vend ON v.id_vendedor = vend.id_vendedor
INNER JOIN productos p ON v.id_producto = p.id_producto
GROUP BY vend.id_vendedor, vend.nombre, vend.apellido, vend.departamento
ORDER BY ganancia_neta DESC;`,
    difficulty: 'advanced',
    why: {
      title: '¿Por qué este ejercicio es avanzado?',
      content: `Este ejercicio integra múltiples conceptos avanzados: **múltiples JOINs**, **funciones de agregación complejas**, **expresiones aritméticas anidadas**, y **cálculo de porcentajes**. 

La complejidad viene de calcular **múltiples métricas financieras** (ingresos, costos, ganancia neta, margen porcentual) en una sola consulta, cada una requiriendo expresiones que dependen de otras. Esto refleja análisis financieros reales que requieren cálculos complejos.

### ¿Por qué es importante calcular métricas financieras en SQL?
- **Análisis en tiempo real**: Cálculos directos en la base de datos
- **Precisión**: Evita errores de cálculo manual
- **Eficiencia**: Una consulta obtiene todas las métricas
- **Consistencia**: Misma lógica de cálculo en todos los reportes

### En el mundo real:
Cuando un CFO necesita un análisis de rentabilidad por vendedor con ingresos, costos, ganancia neta y margen porcentual, integra múltiples JOINs y expresiones complejas en una sola consulta para obtener todas las métricas financieras necesarias.`,
      keyPoints: [
        'Las expresiones complejas pueden calcular múltiples métricas relacionadas',
        'Las funciones de agregación procesan grupos eficientemente',
        'Los cálculos de porcentaje requieren expresiones aritméticas anidadas',
        'Múltiples JOINs permiten obtener datos de diferentes tablas para cálculos'
      ],
      realWorldExample: 'Cuando un analista financiero necesita calcular ROI, margen y ganancia neta por producto o vendedor, integra múltiples expresiones complejas en una sola consulta estructurada.'
    },
    theory: `# Expresiones Complejas: Cálculo de Métricas Financieras

## Cálculos Financieros en SQL

SQL puede calcular métricas financieras complejas directamente en la consulta:

### Ingresos Totales
\`\`\`sql
SUM(v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) AS total_ingresos
\`\`\`
Cantidad × Precio × (1 - Descuento%)

### Costos Totales
\`\`\`sql
SUM(v.cantidad * p.costo_unitario) AS total_costos
\`\`\`
Cantidad × Costo

### Ganancia Neta
\`\`\`sql
SUM(ingresos) - SUM(costos) AS ganancia_neta
\`\`\`
Ingresos - Costos

### Margen Porcentual
\`\`\`sql
((SUM(ingresos) - SUM(costos)) / SUM(ingresos) * 100) AS margen_porcentual
\`\`\`
((Ingresos - Costos) / Ingresos) × 100

## Ejemplo Completo del Ejercicio

\`\`\`sql
SELECT 
    CONCAT(vend.nombre, ' ', vend.apellido) AS nombre_vendedor,
    vend.departamento,
    SUM(v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) AS total_ingresos,
    SUM(v.cantidad * p.costo_unitario) AS total_costos,
    SUM(v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) - 
    SUM(v.cantidad * p.costo_unitario) AS ganancia_neta,
    ((SUM(...) - SUM(...)) / SUM(...) * 100) AS margen_porcentual
FROM ventas v
INNER JOIN vendedores vend ON v.id_vendedor = vend.id_vendedor
INNER JOIN productos p ON v.id_producto = p.id_producto
GROUP BY vend.id_vendedor, vend.nombre, vend.apellido, vend.departamento
ORDER BY ganancia_neta DESC;
\`\`\`

Este query calcula:
1. **Ingresos**: Ventas con descuentos aplicados
2. **Costos**: Costo de productos vendidos
3. **Ganancia Neta**: Ingresos - Costos
4. **Margen %**: (Ganancia / Ingresos) × 100

## Expresiones Anidadas

Las expresiones pueden ser complejas y anidadas:
\`\`\`sql
((A - B) / A * 100)
\`\`\`
Calcula porcentaje de diferencia.

## Ventajas

- **Análisis Completo**: Todas las métricas en una consulta
- **Precisión**: Cálculos exactos sin errores manuales
- **Eficiencia**: Procesa grandes volúmenes eficientemente
- **Consistencia**: Misma lógica en todos los reportes`,
    tips: [
      'Las expresiones complejas pueden calcular múltiples métricas relacionadas',
      'Usa paréntesis para controlar el orden de operaciones',
      'Las funciones de agregación procesan grupos antes de calcular expresiones',
      'Los cálculos de porcentaje requieren división y multiplicación por 100'
    ],
    advice: [
      'Practica creando expresiones complejas para métricas financieras',
      'Usa paréntesis para asegurar el orden correcto de operaciones',
      'Combina múltiples expresiones para calcular métricas relacionadas',
      'Considera la legibilidad al crear expresiones complejas'
    ]
  },
  {
    id: 30,
    level: 'AVANZADO',
    title: 'Ejercicio 30: Comparativo Mensual de Ventas',
    description: 'Comparar ventas de cada mes con el mes anterior usando window functions.',
    expectedColumns: ['mes', 'total_ventas_mes', 'ventas_mes_anterior', 'diferencia', 'crecimiento_porcentual'],
    solution: `WITH ventas_mensuales AS (
    SELECT 
        DATE_FORMAT(fecha_venta, '%Y-%m') AS mes,
        SUM(v.cantidad * p.precio_unitario * (1 - v.descuento_aplicado/100)) AS total_ventas_mes
    FROM ventas v
    INNER JOIN productos p ON v.id_producto = p.id_producto
    GROUP BY DATE_FORMAT(fecha_venta, '%Y-%m')
)
SELECT 
    mes,
    total_ventas_mes,
    LAG(total_ventas_mes) OVER (ORDER BY mes) AS ventas_mes_anterior,
    (total_ventas_mes - LAG(total_ventas_mes) OVER (ORDER BY mes)) AS diferencia,
    ((total_ventas_mes - LAG(total_ventas_mes) OVER (ORDER BY mes)) / 
     LAG(total_ventas_mes) OVER (ORDER BY mes) * 100) AS crecimiento_porcentual
FROM ventas_mensuales
ORDER BY mes;`,
    difficulty: 'advanced',
    why: {
      title: '¿Por qué usar LAG() (Window Function) aquí?',
      content: `**LAG()** es una función de ventana que accede al valor de una fila anterior en el conjunto de resultados. Permite comparar valores actuales con valores anteriores sin usar JOINs complejos o subconsultas.

En este ejercicio, usamos **LAG()** para comparar las ventas de cada mes con las del mes anterior. Esto permite calcular crecimiento, diferencias y tendencias temporales de forma eficiente y legible.

### ¿Por qué LAG() en lugar de JOINs o subconsultas?
- **Simplicidad**: Más simple que JOINs auto-referenciados
- **Performance**: Optimizado para comparaciones secuenciales
- **Legibilidad**: Código más claro y fácil de entender
- **Flexibilidad**: Fácil cambiar el offset (mes anterior, 2 meses atrás, etc.)

### En el mundo real:
Cuando un analista necesita un reporte de crecimiento mes a mes comparando cada mes con el anterior para identificar tendencias, usa LAG() para acceder al valor del mes anterior y calcular diferencias y porcentajes de crecimiento.`,
      keyPoints: [
        'LAG() accede al valor de una fila anterior en el orden especificado',
        'Window functions permiten comparaciones temporales sin JOINs complejos',
        'LAG() es útil para análisis de crecimiento y tendencias',
        'OVER especifica cómo se ordenan las filas para la comparación'
      ],
      realWorldExample: 'Cuando un gerente necesita un reporte de crecimiento de ventas mes a mes para identificar tendencias y tomar decisiones estratégicas, usa LAG() para comparar cada mes con el anterior.'
    },
    theory: `# LAG(): Comparando con Valores Anteriores

## ¿Qué es LAG()?

**LAG()** es una función de ventana que accede al valor de una fila anterior en el conjunto de resultados ordenado. Permite comparar valores actuales con anteriores.

### Sintaxis:
\`\`\`sql
LAG(columna) OVER (ORDER BY columna_orden)
\`\`\`

## Cómo Funciona LAG()

LAG() devuelve el valor de la fila anterior según el orden especificado:

\`\`\`sql
SELECT mes, ventas,
       LAG(ventas) OVER (ORDER BY mes) AS ventas_mes_anterior
FROM ventas_mensuales;
\`\`\`

Para cada mes, muestra las ventas del mes anterior.

## Ejemplo Completo del Ejercicio

\`\`\`sql
WITH ventas_mensuales AS (
    SELECT DATE_FORMAT(fecha_venta, '%Y-%m') AS mes,
           SUM(...) AS total_ventas_mes
    FROM ventas v INNER JOIN productos p ON v.id_producto = p.id_producto
    GROUP BY DATE_FORMAT(fecha_venta, '%Y-%m')
)
SELECT 
    mes,
    total_ventas_mes,
    LAG(total_ventas_mes) OVER (ORDER BY mes) AS ventas_mes_anterior,
    (total_ventas_mes - LAG(total_ventas_mes) OVER (ORDER BY mes)) AS diferencia,
    ((total_ventas_mes - LAG(...)) / LAG(...) * 100) AS crecimiento_porcentual
FROM ventas_mensuales
ORDER BY mes;
\`\`\`

Este query:
1. **Agrupa** ventas por mes en el CTE
2. **Usa LAG()** para obtener ventas del mes anterior
3. **Calcula** diferencia (actual - anterior)
4. **Calcula** crecimiento porcentual ((diferencia / anterior) × 100)

## Cálculo de Crecimiento Porcentual

\`\`\`sql
((actual - anterior) / anterior * 100) AS crecimiento_porcentual
\`\`\`
- Si positivo: crecimiento
- Si negativo: decrecimiento
- Si 0: sin cambio

## Casos de Uso Comunes

### Análisis de Tendencias
\`\`\`sql
SELECT mes, ventas,
       LAG(ventas) OVER (ORDER BY mes) AS mes_anterior,
       ventas - LAG(ventas) OVER (ORDER BY mes) AS cambio
FROM ventas_mensuales;
\`\`\`

### Comparaciones Temporales
\`\`\`sql
SELECT fecha, valor,
       LAG(valor, 7) OVER (ORDER BY fecha) AS semana_anterior
FROM metricas_diarias;
\`\`\`
Compara con valor de 7 días atrás.

## Ventajas de LAG()

- **Simplicidad**: Más simple que JOINs auto-referenciados
- **Performance**: Optimizado para comparaciones secuenciales
- **Flexibilidad**: Puedes especificar offset (mes anterior, año anterior, etc.)
- **Análisis Temporal**: Ideal para análisis de tendencias y crecimiento`,
    tips: [
      'LAG() accede al valor de la fila anterior según el orden en OVER',
      'Puedes especificar offset: LAG(valor, 2) para 2 filas atrás',
      'LAG() devuelve NULL para la primera fila (no hay anterior)',
      'Útil para análisis de crecimiento, tendencias y comparaciones temporales'
    ],
    advice: [
      'Usa LAG() cuando necesites comparar valores con períodos anteriores',
      'Combina LAG() con expresiones para calcular crecimiento y diferencias',
      'Practica usando LAG() con diferentes offsets para análisis temporales',
      'Considera LAG() para análisis de tendencias y reportes de crecimiento'
    ]
  }
];

